<template>
  <div class="detail-image-edit">
    <!-- 로딩 중일 때 표시 -->
    <div v-if="loading" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || '상세 이미지 작업 중...' }}</p>
    </div>

    <!-- 미리보기 모드 UI -->
    <div v-else-if="isPreviewMode" class="preview-mode">
      <div class="editor-header">
        <div class="editor-title">
          <h5>상세 페이지 미리보기</h5>
        </div>
        <div class="editor-actions">
          <b-button variant="outline-primary" size="sm" @click="switchToEditMode">
            <i class="las la-edit"></i> 편집 모드로 전환
          </b-button>
          <b-button variant="success" size="sm" @click="saveDetailPage" title="저장" class="ml-2">
            <i class="las la-save"></i> 저장
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="goToNextStep" title="다음 단계" class="ml-2">
            <i class="las la-arrow-right"></i> 다음 단계
          </b-button>
        </div>
      </div>

      <div class="preview-container">
        <div class="preview-scroll-container">
          <div class="detail-content-preview">
            <div v-html="compiledHtml"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 편집 모드 UI -->
    <div v-else class="edit-mode">
      <!-- 상단 헤더 영역 -->
      <div class="editor-header">
        <div class="editor-title">
          <h5>상세 페이지 이미지</h5>
        </div>
        <div class="editor-actions">
          <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showImageUpload" title="이미지 업로드">
            <i class="las la-upload"></i> 업로드
          </b-button>
          <b-button variant="outline-primary" size="sm" class="mr-2" @click="downloadDetailImage" title="통합 이미지 다운로드">
            <i class="las la-download"></i> 다운로드
          </b-button>
          <b-button variant="outline-warning" size="sm" class="mr-2" @click="translateAllEnglishImages" :disabled="detailImages.length === 0" title="영어 이미지를 한국어로 번역">
            <i class="las la-language"></i> 영어→한국어 번역
          </b-button>
          <b-button variant="outline-success" size="sm" class="mr-2" @click="processAllImages" :disabled="detailImages.length === 0">
            <i class="las la-magic"></i> 모든 이미지 AI 처리
          </b-button>
          <b-button v-if="splitSiblings.length > 0" variant="outline-info" size="sm" class="mr-2"
                    @click="openSiblingApply" :disabled="detailImages.length === 0"
                    title="같은 1688 상품에서 나뉜 다른 건에도 같은 상세페이지를 적용합니다">
            <i class="las la-copy"></i> 나뉜 다른 건에도 적용 ({{ splitSiblings.length }})
          </b-button>
          <b-button variant="success" size="sm" @click="saveDetailPage">
            <i class="las la-save"></i> 저장
          </b-button>
        </div>
      </div>

      <!-- 이미지 문구를 원문과 다르게 고친 곳(소재·인증·최상급) — 맞는지 사람이 확인한다 -->
      <b-alert v-if="claimWarning" show variant="warning" class="claim-warning mb-2">
        <div class="claim-warning__body">
          <div class="claim-warning__title">
            <i class="las la-exclamation-triangle"></i> 이미지 문구 {{ claimWarning.items.length }}곳을 원문과 다르게 고쳤습니다 — 1688 상품 정보에 맞췄으니 맞는지 확인하세요
          </div>
          <ul class="claim-warning__list mb-0">
            <li v-for="(w, i) in claimWarning.items" :key="i">
              {{ w.image }}번 이미지: «{{ w.before }}» → «{{ w.after }}»
              <span class="claim-warning__reason">({{ (w.reasons || []).join(', ') }})</span>
            </li>
          </ul>
        </div>
      </b-alert>

      <div class="editor-content">
        <!-- 일반 이미지 뷰어 -->
        <div class="image-viewer-container">
          <!-- 번역된 이미지가 있는 경우 -->
          <div v-if="processedGroups.length > 0" class="processed-images-section">
            <div class="processed-images-scroller">
              <div class="processed-images-container">
                <!-- 처리된 이미지 (AI 번역 완료) -->
                <div v-for="(group, arrayIndex) in processedGroups" :key="`processed-${group.index}`"
                     class="processed-image-item"
                     :class="{
                       'original-image-item': group.processType === 'original',
                       'setting-image-item': group.processType === 'setting',
                       'top-image-item': group.isTopImage,
                       'bottom-image-item': group.isBottomImage
                     }"
                     @mouseenter="setHoveredImage(group.index, group.processType)"
                     @mouseleave="clearHoveredImage">
                  <!-- 이미지 타입 표시 배지 -->
                  <div v-if="group.processType === 'setting'" class="image-type-badge"
                       :class="{'top-badge': group.isTopImage, 'bottom-badge': group.isBottomImage}">
                    {{ group.isTopImage ? '상단 이미지' : '하단 이미지' }}
                  </div>
                  <div v-else-if="group.processType === 'original'" class="image-type-badge original-badge">
                    원본 이미지
                  </div>
                  <div v-else class="image-type-badge translated-badge">
                    번역된 이미지
                  </div>

                  <!-- 로딩 스피너 오버레이 -->
                  <div v-if="isImageLoading(group.index)" class="loading-overlay">
                    <div class="loading-spinner">
                      <b-spinner variant="primary" label="처리 중..."></b-spinner>
                      <div class="loading-text mt-2">처리 중...</div>
                    </div>
                  </div>

                  <img :src="group.url"
                       :alt="group.processType === 'setting' ? (group.isTopImage ? '상단 이미지' : '하단 이미지') :
                            (group.processType === 'original' ? '원본 이미지' : '번역된 이미지')"
                       class="processed-image"
                       :class="{ 'loading': isImageLoading(group.index) }">

                  <!-- 마우스 오버 시 오버레이 - 다양한 액션 버튼 표시 -->
                  <div class="hover-overlay">
                    <div class="overlay-actions">
                      <!-- 상단/하단 이미지인지 확인 -->
                      <template v-if="group.processType === 'setting'">
                        <div class="text-center p-2 bg-light rounded">
                          <small class="text-muted">설정에서 업로드된 {{ group.isTopImage ? '상단' : '하단' }} 이미지입니다</small>
                        </div>
                      </template>
                      <template v-else>
                        <!-- 원본 이미지와 처리된 이미지에 따라 다른 버튼 보여줌 -->
                        <!-- 이미 편집된 이미지의 경우 -->
                        <b-button
                          v-if="group.processType !== 'original' && group.requestId"
                          variant="primary"
                          size="md"
                          @click="editProcessedGroup(arrayIndex)"
                          class="action-button"
                        >
                          <i class="las la-edit"></i> 편집하기
                        </b-button>
                        <!-- 처리된 이미지지만 requestId가 없는 경우 또는 원본 이미지인 경우 -->
                        <template v-else>
                          <b-button
                            variant="success"
                            size="md"
                            @click="translateSingleImage(group.url, group.index)"
                            class="action-button mb-2"
                          >
                            <i class="las la-magic"></i> 중국어→한국어 번역
                          </b-button>
                          <b-button
                            variant="warning"
                            size="md"
                            @click="translateEnglishSingleImage(group.url, group.index)"
                            class="action-button"
                          >
                            <i class="las la-language"></i> 영어→한국어 번역
                          </b-button>
                        </template>
                      </template>

                      <!-- 관리 버튼 그룹 -->
                      <div class="manage-buttons">
                        <!-- 위로 이동 -->
                        <b-button
                          class="action-icon-button action-icon-up"
                          variant="light"
                          size="sm"
                          @click.stop="moveImageUp(group.index)"
                          :disabled="arrayIndex === 0 || group.processType === 'setting'"
                          title="위로 이동"
                        >
                          <i class="las la-arrow-up"></i>
                        </b-button>

                        <!-- 아래로 이동 -->
                        <b-button
                          class="action-icon-button action-icon-down"
                          variant="light"
                          size="sm"
                          @click.stop="moveImageDown(group.index)"
                          :disabled="arrayIndex === processedGroups.length - 1 || group.processType === 'setting'"
                          title="아래로 이동"
                        >
                          <i class="las la-arrow-down"></i>
                        </b-button>

                        <!-- 삭제 (설정 이미지와 결과 이미지는 삭제 불가) -->
                        <b-button
                          v-if="group.processType !== 'setting'"
                          class="action-icon-button action-icon-delete"
                          variant="light"
                          size="sm"
                          @click.stop="group.processType === 'original' ? deleteOriginalImage(group.index) : deleteImage(group.index)"
                          title="이미지 삭제"
                        >
                          <i class="las la-trash"></i>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- 원본 이미지와 처리된 이미지 모두 processedGroups 안에 포함됨 -->
              </div>
            </div>
          </div>

          <!-- 원본 이미지만 있는 경우 (번역된 이미지가 없을 때) -->
          <div v-else class="original-images-section">
            <div class="original-images-scroller">
              <div class="original-images-container">
                <!-- 이미지 목록 -->
                <div v-for="(imageUrl, index) in detailImages" :key="`img-${index}`"
                     class="original-image-item"
                     @mouseenter="setHoveredImage(index, isTopOrBottomImage(imageUrl) ? 'setting' : 'original')"
                     @mouseleave="clearHoveredImage">
                  <img :src="imageUrl" alt="원본 이미지" class="original-image">

                  <!-- 마우스 오버 시 AI 처리 버튼 (원본 이미지용) -->
                  <div class="hover-overlay">
                    <div class="overlay-actions">
                      <!-- 상단/하단 이미지인지 확인 -->
                      <template v-if="isTopOrBottomImage(imageUrl)">
                        <div class="text-center p-2 bg-light rounded">
                          <small class="text-muted">설정에서 업로드된 이미지입니다</small>
                        </div>
                      </template>
                      <template v-else>
                        <!-- 메인 액션 버튼 -->
                        <b-button
                          variant="success"
                          size="md"
                          @click="translateSingleImage(imageUrl, index)"
                          class="action-button mb-2"
                        >
                          <i class="las la-magic"></i> 중국어→한국어 번역
                        </b-button>
                        <b-button
                          variant="warning"
                          size="md"
                          @click="translateEnglishSingleImage(imageUrl, index)"
                          class="action-button"
                        >
                          <i class="las la-language"></i> 영어→한국어 번역
                        </b-button>
                      </template>

                      <!-- 관리 버튼 그룹 -->
                      <div class="manage-buttons">
                        <!-- 위로 이동 -->
                        <b-button
                          class="action-icon-button action-icon-up"
                          variant="light"
                          size="sm"
                          @click.stop="moveOriginalImageUp(index)"
                          :disabled="index === 0"
                          title="위로 이동"
                        >
                          <i class="las la-arrow-up"></i>
                        </b-button>

                        <!-- 아래로 이동 -->
                        <b-button
                          class="action-icon-button action-icon-down"
                          variant="light"
                          size="sm"
                          @click.stop="moveOriginalImageDown(index)"
                          :disabled="index === detailImages.length - 1"
                          title="아래로 이동"
                        >
                          <i class="las la-arrow-down"></i>
                        </b-button>

                        <!-- 원본 이미지 삭제 -->
                        <b-button
                          class="action-icon-button action-icon-delete"
                          variant="light"
                          size="sm"
                          @click.stop="deleteOriginalImage(index)"
                          title="이미지 삭제"
                        >
                          <i class="las la-trash"></i>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 이미지가 없는 경우 -->
            <div v-if="detailImages.length === 0" class="no-images-placeholder">
              <i class="las la-image"></i>
              <p>등록된 상세 이미지가 없습니다</p>
              <p class="text-muted small">이미지를 업로드한 후 'AI 처리' 버튼을 눌러 이미지를 번역하세요</p>
              <b-button variant="outline-primary" size="sm" @click="showImageUpload" class="mt-3">
                <i class="las la-upload"></i> 이미지 업로드
              </b-button>
            </div>
          </div>
        </div>

        <!-- 로딩 오버레이 -->
        <div v-if="isProcessing" class="loading-overlay">
          <div class="loading-content">
            <b-spinner variant="primary" label="처리 중..."></b-spinner>
            <p>{{ processingMessage || '이미지를 처리 중입니다...' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 이미지 업로드를 위한 숨겨진 input -->
    <input ref="imageUploadInput" type="file" accept="image/*" multiple class="d-none" @change="handleImageUpload">

    <!-- 이미지 편집기 모달 -->
    <b-modal
      v-model="showEditor"
      size="xl"
      hide-footer
      title="이미지 편집"
      centered
      modal-class="image-editor-modal"
      body-class="image-editor-modal-body">
      <ImageEditor
        ref="imageEditor"
        :requestId="editorRequestId"
        :imageUrl="editorImageUrl"
        imageType="detail"
        @image-edited="handleEditorImageEdited"
        @error="handleEditorError"
      />
    </b-modal>

    <!-- 나뉜 다른 건에 상세페이지 적용 모달 -->
    <b-modal
      v-model="showSiblingModal"
      title="나뉜 다른 건에도 적용"
      centered
      :busy="isPropagating"
      ok-title="적용"
      cancel-title="취소"
      @ok.prevent="applyDetailToSiblings">
      <p class="mb-2">
        지금 보고 계신 건의 상세페이지를 아래 건들에도 똑같이 적용합니다.
      </p>
      <p class="text-muted small mb-3">
        쿠팡으로 등록 전송이 끝난 건은 실수로 덮이지 않도록 기본적으로 빠져 있습니다. 필요하시면 직접 체크해 주세요.
      </p>
      <div class="sibling-list">
        <div v-for="target in siblingTargets" :key="target.id" class="sibling-item">
          <b-form-checkbox v-model="target.checked">
            <span class="sibling-name">{{ target.productName }}</span>
            <b-badge v-if="target.registered" variant="warning" class="ml-1">등록 전송됨</b-badge>
          </b-form-checkbox>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ImageEditor from './ImageEditor.vue'
import { mapState, mapActions } from 'vuex'
import detailImageService from '../../services/works/detailImageService'
import { claimBasisOf, buildClaimWarning } from '../../services/works/detailClaimGuard'
import { englishTranslateTargets } from '../../services/works/detailEnglishTargets'
import { detailAltText, needsDetailAltText } from '../../services/works/detailAltText'
import imageService from '../../services/imageService'
import { isLightDoc } from '../../utils/rocketRegLight'

export default {
  name: "DetailImageEdit",
  components: {
    ImageEditor
  },
  props: {
    wizardData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTab: 'detail',
      isPreviewMode: false,
      detailHtml: '', // 상세 페이지 HTML
      imageMargin: 0,
      isProcessing: false,
      processingMessage: '',

      // 개별 이미지 로딩 상태 관리
      imageLoadingStates: {}, // { index: true/false }

      // 이미지 선택 관련
      currentGroupIndex: -1, // 현재 선택된 그룹 인덱스
      showProcessedImage: false, // 처리된 이미지 보기 모드 여부

      // 이미지 처리 관련
      originalImages: [], // 원본 이미지 배열
      // 처리된 이미지 그룹 - 원래 인덱스 위치를 유지하기 위해 명시적으로 객체로 관리
      processedGroupsMap: {}, // key: 원본 인덱스, value: 처리된 이미지 객체

      // 편집기 관련
      showEditor: false,
      editorRequestId: '',
      editorImageUrl: '',
      currentEditingIndex: -1,

      // 최근 수정 시간
      lastModifiedDate: null,

      // 마우스 hover 중인 이미지 정보 (Delete 키 삭제용)
      hoveredImageIndex: null,
      hoveredProcessType: null,

      // 나뉜 다른 건에 상세페이지 적용
      showSiblingModal: false,
      siblingTargets: [],
      isPropagating: false
    };
  },
  computed: {
    // 이미지 문구 정정 경고 — 저장할 때와 같은 이미지 순서(상단/하단 설정 이미지 제외)로 모은다
    claimWarning() {
      return buildClaimWarning(this.processedGroups.filter(group => group && group.url && !group.isSettingImage));
    },
    // Vuex 상태 매핑
    ...mapState({
      product: state => state.selectedRocketProduct,
      rocketProducts: state => state.rocketProducts,
      processing: state => state.processing,
      progressMessage: state => state.progressMessage,
      defaultSettings: state => {
        return state.settings && state.settings[0] && state.settings[0].defaultSettings
          ? state.settings[0].defaultSettings
          : {};
      }
    }),

    // 로딩 상태
    loading() {
      return this.processing;
    },

    // 상세 이미지 목록
    detailImages() {
      return this.product?.detailImages || [];
    },

    // 작업개시의 견적서 단계에서 같은 1688 상품이 옵션 묶음별로 나뉜 형제 건들
    // (등록번호가 원본 = 260916002001, 나뉜 건 = 260916002001_2 ... 형태로 붙는다)
    splitSiblings() {
      const current = this.product;
      if (!current || !current.registrationId) return [];

      const baseId = this.getSplitBaseId(current.registrationId);
      const currentLink = current.구매링크;

      return (this.rocketProducts || []).filter(candidate => {
        if (!candidate || !candidate.registrationId) return false;
        // 자기 자신 제외 (_id 가 없는 신규 건은 등록번호로 비교)
        if (candidate._id && current._id) {
          if (String(candidate._id) === String(current._id)) return false;
        } else if (candidate.registrationId === current.registrationId) {
          return false;
        }
        if (this.getSplitBaseId(candidate.registrationId) !== baseId) return false;
        // 등록번호 앞부분이 같아도 1688 링크가 다르면 다른 상품이다
        if (currentLink && candidate.구매링크 && candidate.구매링크 !== currentLink) return false;
        return true;
      });
    },

    // processedGroups: 모든 이미지(원본과 처리된 이미지)를 함께 표시
    // 처리된 이미지가 있으면 처리된 이미지를, 없으면 원본 이미지를 표시
    processedGroups() {
      // 1. 모든 이미지를 포함할 결과 배열 생성
      const result = [];

      console.log('processedGroups 계산 중 - 원본 이미지 수:', this.detailImages.length);
      console.log('processedGroups 계산 중 - 처리된 이미지 맵 키:', Object.keys(this.processedGroupsMap).sort());

      // 설정에서 상단/하단 이미지 URL 가져오기
      const settings = this.defaultSettings.imageSettings || {};
      const topImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
      const bottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

      // 상단/하단 이미지 추가 여부 추적
      let hasTopImage = false;
      let hasBottomImage = false;

      // 결과 이미지 관련 코드 제거

      // 작업 후 상태인지 확인 (processedDetailImages가 있는 경우)
      const isProcessed = this.product && this.product.processedDetailImages && this.product.processedDetailImages.length > 0;

      // 작업 전 상태이면 원래 방식대로 처리
      if (!isProcessed) {
        // 2. 원본 이미지 순서대로 검사하여 처리된 이미지나 원본 이미지를 추가
        for (let i = 0; i < this.detailImages.length; i++) {
          const originalUrl = this.detailImages[i];

          // 상단/하단 이미지인지 확인
          const isTopImage = originalUrl === topImageUrl;
          const isBottomImage = originalUrl === bottomImageUrl;

          // 상단/하단 이미지인 경우 특별 처리 (중복 방지)
          if (isTopImage) {
            if (!hasTopImage) { // 아직 상단 이미지가 추가되지 않은 경우에만 추가
              console.log(`인덱스 ${i}는 상단 이미지, 원본 그대로 추가`);
              result.push({
                index: i,
                url: originalUrl,
                originalUrls: [originalUrl],
                processType: 'setting', // 설정 이미지임을 표시
                isSettingImage: true,
                isTopImage: true,
                isBottomImage: false,
                isProcessed: false
              });
              hasTopImage = true; // 상단 이미지 추가됨
            } else {
              console.log(`인덱스 ${i}는 상단 이미지이지만 이미 추가되어 있어 건너뜀`);
            }
            continue;
          } else if (isBottomImage) {
            if (!hasBottomImage) { // 아직 하단 이미지가 추가되지 않은 경우에만 추가
              console.log(`인덱스 ${i}는 하단 이미지, 원본 그대로 추가`);
              result.push({
                index: i,
                url: originalUrl,
                originalUrls: [originalUrl],
                processType: 'setting', // 설정 이미지임을 표시
                isSettingImage: true,
                isTopImage: false,
                isBottomImage: true,
                isProcessed: false
              });
              hasBottomImage = true; // 하단 이미지 추가됨
            } else {
              console.log(`인덱스 ${i}는 하단 이미지이지만 이미 추가되어 있어 건너뜀`);
            }
            continue;
          }

          // 해당 인덱스에 처리된 이미지가 있으면 그것을 사용
          if (this.processedGroupsMap[i]) {
            console.log(`인덱스 ${i}의 처리된 이미지 발견, 결과에 추가`);
            result.push(this.processedGroupsMap[i]);
          }
          // 처리된 이미지가 없으면 원본 이미지 정보로 유사한 객체 생성
          else {
            console.log(`인덱스 ${i}에는 처리된 이미지가 없음, 원본 이미지 추가`);
            // 원본 이미지 정보로 임시 객체 생성
            result.push({
              index: i,
              url: originalUrl,
              originalUrls: [originalUrl],
              processType: 'original', // 원본 이미지임을 표시
              isProcessed: false
            });
          }
        }
      }
      // 작업 후 상태이면 상단 이미지 + 작업한 이미지들 + 하단 이미지 순으로 표시
      else {
        // 1. 상단 이미지 추가 (있는 경우)
        if (topImageUrl) {
          console.log('상단 이미지 추가');
          result.push({
            index: -1, // 특별 인덱스
            url: topImageUrl,
            originalUrls: [topImageUrl],
            processType: 'setting', // 설정 이미지임을 표시
            isSettingImage: true,
            isTopImage: true,
            isBottomImage: false,
            isProcessed: false
          });
          hasTopImage = true;
        }

        // 2. 작업한 이미지들 추가 (상단/하단 이미지 제외)
        for (const key in this.processedGroupsMap) {
          const processedImage = this.processedGroupsMap[key];
          const processedUrl = processedImage.url;

          // 상단/하단 이미지인지 확인
          const isProcessedTopImage = processedUrl === topImageUrl;
          const isProcessedBottomImage = processedUrl === bottomImageUrl;

          // 상단/하단 이미지인 경우 건너뜀
          if (isProcessedTopImage || isProcessedBottomImage) {
            console.log(`처리된 이미지 중 ${isProcessedTopImage ? '상단' : '하단'} 이미지는 건너뜀`);
            continue;
          }

          console.log(`처리된 이미지 추가: 인덱스 ${processedImage.index}`);
          result.push(processedImage);
        }

        // 3. 하단 이미지 추가 (있는 경우)
        if (bottomImageUrl) {
          console.log('하단 이미지 추가');
          result.push({
            index: -3, // 특별 인덱스
            url: bottomImageUrl,
            originalUrls: [bottomImageUrl],
            processType: 'setting', // 설정 이미지임을 표시
            isSettingImage: true,
            isTopImage: false,
            isBottomImage: true,
            isProcessed: false
          });
          hasBottomImage = true;
        }
      }

      console.log('processedGroups 최종 결과:', result.map(g => {
        if (g.processType === 'setting') return g.isTopImage ? `상단이미지-${g.index}` : `하단이미지-${g.index}`;
        return g.processType === 'original' ? `원본-${g.index}` : `처리됨-${g.index}`;
      }));
      return result;
    },

    // 컴파일된 HTML (미리보기용)
    compiledHtml() {
      return this.detailHtml;
    },

    // 이미지를 5개씩 그룹화 (계산된 속성)
    imageGroups() {
      const groups = [];
      if (this.detailImages && this.detailImages.length > 0) {
        for (let i = 0; i < this.detailImages.length; i += 5) {
          groups.push(this.detailImages.slice(i, i + 5));
        }
      }
      return groups;
    },
  },
  created() {
    // 상품에 이미 상세 페이지가 있는 경우
    if (this.product && this.product.detailHtml) {
      this.detailHtml = this.product.detailHtml;
      this.lastModifiedDate = this.product.lastModified || new Date();
    } else {
      this.initializeDetailHtml();
    }

    // 처리된 이미지가 있으면 불러오기 - processedGroupsMap 으로 변환하여 저장
    if (this.product && this.product.processedDetailImages) {
      const processedImages = this.product.processedDetailImages;

      // 맵으로 변환
      this.processedGroupsMap = {};
      processedImages.forEach(img => {
        if (img && img.index !== undefined) {
          this.$set(this.processedGroupsMap, img.index, img);
        }
      });

      console.log('초기 처리된 이미지 맵 생성 완료:', Object.keys(this.processedGroupsMap));
    }
  },
  mounted() {
    console.log('DetailImageEdit 마운트됨, 선택된 제품:', this.product);

    // 타이머를 이용해 컴포넌트가 완전히 마운트된 후 첫 이미지 그룹 선택
    setTimeout(() => {
      if (this.processedGroups.length > 0 && this.currentGroupIndex === -1) {
        console.log('타이머: 첫 번째 처리된 이미지 그룹 선택 (총 ', this.processedGroups.length, '개)');
        this.selectProcessedGroup(0);
      } else if (this.imageGroups.length > 0 && this.currentGroupIndex === -1) {
        console.log('타이머: 첫 번째 원본 이미지 그룹 선택');
        this.selectOriginalGroup(0);
      }
    }, 500);

    // Delete 키 이벤트 리스너 등록
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    // Delete 키 이벤트 리스너 제거
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    // Vuex 액션 매핑
    ...mapActions([
      'updateRocketProduct',
      'setProcessing'
    ]),

    // 등록번호에서 나뉘기 전 원본 번호를 얻는다 (260916002001_3 → 260916002001)
    getSplitBaseId(registrationId) {
      return String(registrationId || '').replace(/_\d+$/, '');
    },

    // 「나뉜 다른 건에도 적용」 모달 열기
    openSiblingApply() {
      this.siblingTargets = this.splitSiblings.map(sibling => ({
        id: sibling._id || sibling.registrationId,
        registrationId: sibling.registrationId,
        productName: (sibling.seo && sibling.seo.productName) || sibling.productName || sibling.registrationId,
        registered: sibling.registered === true,
        // 이미 쿠팡으로 등록 전송이 끝난 건은 기본 제외
        checked: sibling.registered !== true
      }));
      this.showSiblingModal = true;
    },

    // 현재 건의 상세페이지를 선택한 형제 건들에 그대로 적용
    async applyDetailToSiblings() {
      const selected = this.siblingTargets.filter(target => target.checked);

      if (selected.length === 0) {
        this.$bvToast.toast('적용할 건을 하나 이상 선택해 주세요', {
          title: '알림', variant: 'warning', solid: true,
          toaster: 'b-toaster-bottom-right', autoHideDelay: 2000
        });
        return;
      }

      this.isPropagating = true;

      // 대상은 목록용(_light) 문서라 coupangDoc 이 없다 — 적용 전에 완전 문서를 받는다(못 받으면 멈춘다)
      const findTarget = target => (this.rocketProducts || []).find(
        p => String(p._id || p.registrationId) === String(target.id)
      );
      const fullById = new Map();
      try {
        const ids = selected.map(t => findTarget(t)).filter(p => p && p._id).map(p => String(p._id));
        const fulls = await this.$store.dispatch('ensureFullRocketProducts', ids);
        (fulls || []).forEach(p => fullById.set(String(p._id), p));
        if (ids.some(id => !fullById.has(id) || isLightDoc(fullById.get(id)))) {
          throw new Error('상품 정보를 다 불러오지 못했습니다');
        }
      } catch (error) {
        console.error('상세페이지 적용 대상 불러오기 실패:', error);
        this.$bvToast.toast(`적용할 상품 정보를 불러오지 못해 멈췄습니다: ${error.message}`, {
          title: '오류', variant: 'danger', solid: true,
          toaster: 'b-toaster-bottom-right', autoHideDelay: 3000
        });
        this.isPropagating = false;
        return;
      }

      try {
        // 먼저 현재 건을 저장한다. 통합 이미지가 저장 시점에 만들어지므로
        // 저장하지 않고 복사하면 형제 건에 옛 이미지가 들어간다.
        await this.saveDetailPage();

        const source = this.product;
        const detailImageUrls = [...(source.detailImages || [])];
        const combinedImageUrl =
          source.coupangDoc?.jsonDocument?.[0]?.imagePage?.details?.detailedImage ||
          detailImageUrls.join(',');

        let successCount = 0;
        const failed = [];

        for (const target of selected) {
          // store 에서 다시 찾아 받은 완전 문서를 쓴다
          const listed = findTarget(target);
          const targetProduct = listed && listed._id ? fullById.get(String(listed._id)) : null;

          if (!targetProduct || !targetProduct._id) {
            console.error(`상세페이지 적용 실패 - 대상을 찾을 수 없음: ${target.registrationId}`);
            failed.push(target.productName);
            continue;
          }

          try {
            await this.applyDetailToOneProduct(targetProduct, source, detailImageUrls, combinedImageUrl);
            successCount++;
          } catch (error) {
            console.error(`상세페이지 적용 실패 (${target.registrationId}):`, error);
            failed.push(target.productName);
          }
        }

        this.showSiblingModal = false;

        if (failed.length === 0) {
          this.$bvToast.toast(`${successCount}건에 상세페이지를 적용했습니다`, {
            title: '적용 완료', variant: 'success', solid: true,
            toaster: 'b-toaster-bottom-right', autoHideDelay: 2500
          });
        } else {
          this.$bvToast.toast(`${successCount}건 적용 완료, ${failed.length}건 실패`, {
            title: '일부 적용 실패', variant: 'warning', solid: true,
            toaster: 'b-toaster-bottom-right', autoHideDelay: 4000
          });
        }
      } catch (error) {
        console.error('상세페이지 일괄 적용 중 오류:', error);
        this.$bvToast.toast(`적용 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류', variant: 'danger', solid: true,
          toaster: 'b-toaster-bottom-right', autoHideDelay: 3000
        });
      } finally {
        this.isPropagating = false;
      }
    },

    // 한 건에 상세페이지 복사 (등록문서·옵션까지 같이 맞춘다)
    // altText 는 건마다 상품명·옵션명이 다르므로 대상 것을 그대로 둔다
    async applyDetailToOneProduct(targetProduct, source, detailImageUrls, combinedImageUrl) {
      const updateFields = {
        detailImages: [...detailImageUrls],
        detailHtml: source.detailHtml || '',
        processedDetailImages: JSON.parse(JSON.stringify(source.processedDetailImages || [])),
        detailImageStatus: 'completed',
        detailImageStatusObj: { status: 'completed', images: [...detailImageUrls] },
        originDetailImages: JSON.parse(JSON.stringify(source.originDetailImages || [])),
        lastModified: new Date()
      };

      // 상품 레벨 등록문서
      const targetDocs = targetProduct.coupangDoc?.jsonDocument || [];
      targetDocs.forEach((docItem, docIndex) => {
        if (docItem && docItem.imagePage && docItem.imagePage.details) {
          updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.details.detailedImage`] = combinedImageUrl;
          updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.details.htmlProductDetailContent`] = '';
        }
      });

      // 옵션 레벨 (옵션 수는 건마다 다르므로 대상 건 기준으로 만든다)
      const targetOptions = targetProduct.options || [];
      targetOptions.forEach((option, optionIndex) => {
        updateFields[`options.${optionIndex}.detailImages`] = [...detailImageUrls];
        updateFields[`options.${optionIndex}.detailImageStatus`] = 'completed';
        updateFields[`options.${optionIndex}.detailImageStatusObj`] = {
          status: 'completed',
          images: [...detailImageUrls]
        };

        if (option.coupangDoc?.jsonDocument?.[0]?.imagePage?.details) {
          updateFields[`options.${optionIndex}.coupangDoc.jsonDocument.0.imagePage.details.detailedImage`] = combinedImageUrl;
          updateFields[`options.${optionIndex}.coupangDoc.jsonDocument.0.imagePage.details.htmlProductDetailContent`] = '';
        }
      });

      await this.$store.dispatch('updateProductPartial', {
        productId: targetProduct._id,
        updateFields
      });
    },

    // 키보드 이벤트 핸들러 (Delete 키로 hover된 이미지 삭제)
    handleKeyDown(event) {
      // Delete 키가 눌렸고, hover된 이미지가 있는 경우
      if (event.key === 'Delete' && this.hoveredImageIndex !== null) {
        // 설정 이미지(상단/하단)는 삭제 불가
        if (this.hoveredProcessType === 'setting') {
          return;
        }

        // 원본 이미지 또는 처리된 이미지 삭제
        if (this.hoveredProcessType === 'original') {
          this.deleteOriginalImage(this.hoveredImageIndex);
        } else {
          this.deleteImage(this.hoveredImageIndex);
        }
      }
    },

    // 마우스가 이미지 위에 올라갔을 때
    setHoveredImage(index, processType) {
      this.hoveredImageIndex = index;
      this.hoveredProcessType = processType;
    },

    // 마우스가 이미지에서 벗어났을 때
    clearHoveredImage() {
      this.hoveredImageIndex = null;
      this.hoveredProcessType = null;
    },

    // 상세 페이지 HTML 초기화
    initializeDetailHtml() {
      console.log('initializeDetailHtml 시작');
      if (!this.product) {
        this.detailHtml = '<div style="text-align:center; padding:20px;">상품 정보가 없습니다.</div>';
        return;
      }

      let html = `
      <div style="width:100%; max-width:800px; margin:0 auto; background-color:#ffffff; padding:20px;">
      `;

      // 상단 이미지 추가 (defaultSettings에서 가져옴)
      const settings = this.defaultSettings.imageSettings || {};
      if (settings.useTopImage && settings.topImageUrl) {
        html += `
        <div style="margin-bottom:${this.imageMargin}px; text-align:center;">
          <img src="${settings.topImageUrl}" alt="상단 이미지" style="max-width:100%; height:auto; width:760px; display:block; margin:0 auto;">
        </div>
      `;
      }

      // 상세 이미지 추가
      if (this.detailImages && this.detailImages.length > 0) {
        this.detailImages.forEach((imageUrl, index) => {
          html += `
          <div style="margin-bottom:${this.imageMargin}px; text-align:center;">
            <img src="${imageUrl}" alt="상세 이미지 ${index + 1}" style="max-width:100%; height:auto; width:760px; display:block; margin:0 auto;">
          </div>
        `;
        });
      } else {
        html += '<div style="text-align:center; padding:40px; color:#999; font-size:18px;">상세 이미지가 없습니다.</div>';
      }

      // 하단 이미지 추가 (defaultSettings에서 가져옴)
      if (settings.useBottomImage && settings.bottomImageUrl) {
        html += `
        <div style="margin-bottom:${this.imageMargin}px; text-align:center;">
          <img src="${settings.bottomImageUrl}" alt="하단 이미지" style="max-width:100%; height:auto; width:760px; display:block; margin:0 auto;">
        </div>
      `;
      }

      html += '</div>';
      this.detailHtml = html;
      console.log('initializeDetailHtml 완료, HTML 길이:', html.length);
    },

    // 처리된 이미지 그룹 선택
    selectProcessedGroup(index) {
      this.currentGroupIndex = index;
      this.showProcessedImage = true;

      // 처리된 이미지의 requestId 설정
      if (this.processedGroups[index]) {
        this.editorRequestId = this.processedGroups[index].requestId || '';
      } else {
        this.editorRequestId = '';
      }

      // 편집기 닫기
      this.showEditor = false;
    },

    // 원본 이미지 그룹 선택
    selectOriginalGroup(index) {
      this.currentGroupIndex = index;
      this.showProcessedImage = false;
      this.editorRequestId = '';

      // 편집기 닫기
      this.showEditor = false;
    },

    // 처리된 이미지 그룹 편집
    editProcessedGroup(index) {
      if (this.processedGroups[index]) {
        // 에디터 설정
        this.currentGroupIndex = index;
        this.currentEditingIndex = index;

        // requestId 추출 - 이전 버전 호환성 고려
        let requestId = '';
        const processedGroup = this.processedGroups[index];

        if (processedGroup.requestId) {
          requestId = processedGroup.requestId;
        } else if (processedGroup.translationData) {
          // 다양한 API 응답 형식에서 RequestId 추출 시도
          const data = processedGroup.translationData;
          requestId = data.requestId ||
                    data.RequestId ||
                    (data.Data && data.Data.RequestId) || '';
        }

        this.editorRequestId = requestId;
        this.editorImageUrl = this.processedGroups[index].url;

        console.log('이미지 편집 시작:', {
          그룹인덱스: index,
          RequestId: requestId,
          이미지URL: this.processedGroups[index].url.substring(0, 50) + '...'
        });

        // requestId가 없는 경우 처리
        if (!requestId) {
          this.$bvToast.toast('편집에 필요한 정보가 없습니다. AI 처리를 다시 시도해주세요.', {
            title: '알림',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 3000
          });
          return;
        }

        // 에디터 표시
        this.showEditor = true;
      }
    },

    // 특정 그룹의 이미지들을 AI 처리
    async processImageGroup(index) {
      if (!this.imageGroups[index] || this.imageGroups[index].length === 0) {
        this.$bvToast.toast('처리할 이미지가 없습니다', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
        return;
      }

      try {
        // 로딩 상태 설정
        this.isProcessing = true;
        this.processingMessage = `이미지 그룹 ${index + 1} 번역 처리 중...`;

        const currentGroup = this.imageGroups[index];
        let processedCount = 0;

        // 모든 이미지 개별 처리
        for (let i = 0; i < currentGroup.length; i++) {
          const imageUrl = currentGroup[i];
          this.processingMessage = `이미지 그룹 ${index + 1} 번역 처리 중... (${i+1}/${currentGroup.length})`;

          try {
            console.log(`그룹 ${index + 1}, 이미지 ${i+1} 처리 시작:`, imageUrl);

            const translationResult = await imageService.translateImage(imageUrl);

            console.log(`그룹 ${index + 1}, 이미지 ${i+1} 번역 결과:`, translationResult);

            if (translationResult) {
              // 응답 형식 확인 - RequestId가 어디에 있는지 확인
              const requestId = translationResult.requestId ||
                               translationResult.RequestId ||
                               (translationResult.Data && translationResult.Data.RequestId) || '';

              // 번역된 이미지 URL 확인 - 여기서는 번역된 이미지(SslTranslateUrl)만 사용
              const translatedImageUrl = translationResult.translatedImageUrl ||
                                       (translationResult.Data && translationResult.Data.SslTranslateUrl) ||
                                       (translationResult.Data && translationResult.Data.TranslateUrl) ||
                                       imageUrl; // 번역된 이미지 없으면 원본 사용

              // 프로덕트에서 이 이미지의 전체 인덱스 계산
              const globalIndex = index * 5 + i;

              // 처리된 이미지에 추가 또는 업데이트
              const existingIndex = this.processedGroups.findIndex(g => g.index === globalIndex);

              const processedImage = {
                index: globalIndex,
                url: translatedImageUrl,
                requestId: requestId,
                originalUrls: [imageUrl], // 원본 이미지 참조 저장
                processType: 'translated',
                translationData: translationResult,
                groupSize: 1,
                lastEdited: new Date()
              };

              if (existingIndex >= 0) {
                // 기존 이미지 업데이트
                this.processedGroups.splice(existingIndex, 1, processedImage);
              } else {
                // 새 이미지 추가
                this.processedGroups.push(processedImage);
              }

              processedCount++;
            }
          } catch (error) {
            console.error(`그룹 ${index + 1}, 이미지 ${i+1} 처리 중 오류:`, error);
            // 오류 발생해도 다음 이미지 계속 처리
          }
        }

        // 처리된 이미지가 없는 경우
        if (processedCount === 0) {
          throw new Error('그룹 내 모든 이미지 처리에 실패했습니다');
        }

        // HTML 재생성
        this.generateDetailHtmlFromProcessed();

        // 성공 메시지
        this.$bvToast.toast(`이미지 그룹 ${index + 1}에서 ${processedCount}개 이미지 번역이 완료되었습니다`, {
          title: '처리 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });

        // 처리된 이미지 선택
        this.selectOriginalGroup(index);
      } catch (error) {
        console.error('이미지 그룹 번역 오류:', error);
        this.$bvToast.toast(`이미지 그룹 번역 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 처리 상태 해제
        this.isProcessing = false;
        this.processingMessage = '';
      }
    },

    // 현재 선택된 그룹 AI 처리 (번역)
    async translateCurrentGroup() {
      // 현재 선택된 그룹이 없는 경우
      if (this.currentGroupIndex < 0 || !this.imageGroups[this.currentGroupIndex]) {
        this.$bvToast.toast('처리할 이미지 그룹을 선택해주세요', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
        return;
      }

      // 이미 선택된 그룹이 있으면 해당 그룹 처리
      await this.processImageGroup(this.currentGroupIndex);
    },

    // 에디터 열기
    openEditor() {
      if (this.currentGroupIndex < 0) {
        this.$bvToast.toast('편집할 이미지를 선택해주세요', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
        return;
      }

      // 처리된 이미지 편집
      if (this.showProcessedImage && this.processedGroups[this.currentGroupIndex]) {
        this.editorImageUrl = this.processedGroups[this.currentGroupIndex].url;
        this.editorRequestId = this.processedGroups[this.currentGroupIndex].requestId || '';
        this.currentEditingIndex = this.currentGroupIndex;
        this.showEditor = true;
      }
      // 원본 이미지 그룹 편집 (첫 번째 이미지 선택)
      else if (!this.showProcessedImage && this.imageGroups[this.currentGroupIndex] && this.imageGroups[this.currentGroupIndex].length > 0) {
        this.editorImageUrl = this.imageGroups[this.currentGroupIndex][0];
        this.editorRequestId = '';
        this.currentEditingIndex = this.currentGroupIndex;
        this.showEditor = true;
      }
      else {
        this.$bvToast.toast('편집할 이미지가 없습니다', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
      }
    },

    // 이미지 간격 조정
    applyImageMargin() {
      // 입력값 검증
      const margin = parseInt(this.imageMargin);
      if (isNaN(margin) || margin < 0) {
        this.imageMargin = 0;
        return;
      }

      // HTML에서 이미지 간격 업데이트
      const parser = new DOMParser();
      const doc = parser.parseFromString(this.detailHtml, 'text/html');
      const imageContainers = doc.querySelectorAll('div[style*="margin-bottom"]');

      imageContainers.forEach(container => {
        const style = container.getAttribute('style');
        const updatedStyle = style.replace(/margin-bottom:[^;]+;/, `margin-bottom:${margin}px;`);
        container.setAttribute('style', updatedStyle);
      });

      this.detailHtml = doc.body.innerHTML;

      // 변경 메시지
      this.$bvToast.toast(`이미지 간격이 ${margin}px로 설정되었습니다`, {
        title: '설정 변경',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 1500
      });
    },

    // 원본 이미지 선택
    selectOriginalImage(index) {
      this.selectedOriginalIndex = index;
      this.selectedProcessedIndex = -1;
      // 선택된 원본 이미지 상태 업데이트
      this.showEditor = false;
      this.editorRequestId = '';
      this.editorImageUrl = '';
    },

    // 처리된 이미지 선택 및 편집
    editProcessedImage(index, image) {
      this.selectedProcessedIndex = index;
      this.selectedOriginalIndex = -1;
      this.currentEditingIndex = index;

      // 에디터 초기화
      if (image.requestId) {
        this.editorRequestId = image.requestId;
        this.editorImageUrl = image.url;
        this.showEditor = true;
      } else {
        // requestId가 없는 경우 처리 (필요시 API로 requestId 요청)
        this.handleImageWithoutRequestId(image);
      }
    },

    // requestId가 없는 이미지 처리
    async handleImageWithoutRequestId(image) {
      try {
        this.isProcessing = true;
        this.setProcessing({
          isProcessing: true,
          message: "이미지 정보 가져오는 중..."
        });

        // 이미지 번역 API 호출하여 requestId 획득
        const result = await imageService.translateImage(image.url);

        if (result && result.requestId) {
          // 이미지 정보 업데이트
          this.processedGroups[this.currentEditingIndex].requestId = result.requestId;

          // 에디터 초기화
          this.editorRequestId = result.requestId;
          this.editorImageUrl = image.url;
          this.showEditor = true;
        } else {
          this.$bvToast.toast('이미지 정보를 가져올 수 없습니다', {
            title: '알림',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 3000
          });
        }
      } catch (error) {
        console.error('이미지 requestId 획득 실패:', error);
        this.$bvToast.toast('이미지 정보를 가져오는 중 오류가 발생했습니다', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        this.isProcessing = false;
        this.setProcessing({ isProcessing: false });
      }
    },

    // 에디터 닫기
    closeEditor() {
      // 변경 사항이 있는지 확인
      if (this.$refs.imageEditor && this.$refs.imageEditor.isEditorReady() && this.$refs.imageEditor.getEditResult()) {
        // 변경 사항이 있는 경우 저장 여부 확인
        this.$bvModal.msgBoxConfirm('변경 사항이 있습니다. 저장하지 않고 닫으시겠습니까?', {
          title: '변경 사항 저장',
          okVariant: 'danger',
          okTitle: '저장 안함',
          cancelTitle: '취소',
          hideHeaderClose: false,
          centered: true
        }).then(confirmed => {
          if (confirmed) {
            this.resetEditor();
          }
        });
      } else {
        // 변경 사항이 없는 경우 바로 에디터 초기화
        this.resetEditor();
      }
    },

    // 에디터 상태 초기화
    resetEditor() {
      this.showEditor = false;
      this.editorRequestId = '';
      this.editorImageUrl = '';
      this.currentEditingIndex = -1;
      this.selectedProcessedIndex = -1;
    },

    // 특정 인덱스의 이미지로 스크롤
    scrollToImage(index) {
      this.$nextTick(() => {
        setTimeout(() => {
          const element = document.getElementById(`detail-image-${index}`);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100); // 이미지 로딩 대기
      });
    },

    // 개별 이미지 로딩 상태 설정
    setImageLoading(index, loading) {
      this.$set(this.imageLoadingStates, index, loading);
    },

    // 개별 이미지 로딩 상태 확인
    isImageLoading(index) {
      return this.imageLoadingStates[index] || false;
    },

    // 저장 후 에디터 닫기
    saveAndCloseEditor() {
      if (this.$refs.imageEditor && this.$refs.imageEditor.isEditorReady()) {
        // 에디터가 준비된 상태이고 결과가 있는 경우
        const result = this.$refs.imageEditor.getEditResult();
        if (result && result.url) {
          this.handleEditorImageEdited({
            url: result.url,
            requestId: this.editorRequestId
          });
        } else {
          this.resetEditor();
        }
      } else {
        this.resetEditor();
      }
    },

    // 에디터에서 이미지 편집 완료 이벤트 처리
    handleEditorImageEdited(result) {
      console.log('에디터 이미지 편집 완료:', result);

      // 현재 편집 중인 이미지 정보 업데이트
      if (this.currentEditingIndex >= 0 && this.currentEditingIndex < this.processedGroups.length) {
        // 기존 URL 백업
        const oldUrl = this.processedGroups[this.currentEditingIndex].url;

        // 편집 결과 URL 추출 - API 응답 구조 확인
        let editedUrl = result.url;
        let requestId = result.requestId || this.editorRequestId;

        // API 응답 구조가 다른 경우 처리 - 번역된 이미지 URL 사용(텍스트 제거 이미지 아님)
        if (!editedUrl && result.Data) {
          if (result.Data.SslTranslateUrl || result.Data.TranslateUrl) {
            // 번역된 이미지 URL 우선 사용
            editedUrl = result.Data.SslTranslateUrl || result.Data.TranslateUrl;
          } else if (result.Data.SslRmUrl || result.Data.RmUrl) {
            // 번역된 이미지가 없는 경우에만 텍스트 제거 이미지 사용
            editedUrl = result.Data.SslRmUrl || result.Data.RmUrl;
          }
        }

        if (!requestId && result.RequestId) {
          requestId = result.RequestId;
        }

        console.log('이미지 편집 결과:', {
          oldUrl: oldUrl.substring(0, 30) + '...',
          newUrl: editedUrl ? (editedUrl.substring(0, 30) + '...') : '없음',
          requestId
        });

        // 새 URL 없으면 원본 유지
        if (!editedUrl) {
          console.error('편집된 이미지 URL이 없습니다. 원본 URL 유지');
          editedUrl = oldUrl;
        }

        // 새 URL로 업데이트 - processedGroupsMap 사용
        const currentGroup = this.processedGroups[this.currentEditingIndex];
        if (currentGroup) {
          // 현재 그룹의 인덱스를 찾아서 Map에 업데이트
          const index = currentGroup.index;
          const updatedImage = { ...currentGroup,
            url: editedUrl,
            requestId: requestId,
            lastEdited: new Date()
          };
          this.$set(this.processedGroupsMap, index, updatedImage);
        }

        // HTML에서도 이미지 URL 업데이트 (상세 페이지 HTML에 있을 경우)
        if (this.detailHtml.includes(oldUrl)) {
          this.detailHtml = this.detailHtml.replace(
            new RegExp(oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            editedUrl
          );
        }

        // 변경 메시지
        this.$bvToast.toast('이미지가 편집되었습니다', {
          title: '편집 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500
        });

        // 에디터 닫기
        this.resetEditor();

        // 최근 수정일 업데이트
        this.lastModifiedDate = new Date();

        // HTML 재생성 (이미지 변경 반영)
        this.generateDetailHtmlFromProcessed();

        // 편집한 이미지 위치로 스크롤 복원
        if (this.currentEditingIndex >= 0) {
          this.scrollToImage(this.currentEditingIndex);
        }
      } else {
        console.error('에디터 이미지 편집 완료 후 처리 오류: 현재 편집 중인 인덱스 유효하지 않음', this.currentEditingIndex);

        // 에디터 닫기
        this.resetEditor();

        // 오류 메시지
        this.$bvToast.toast('편집된 이미지를 저장하는 중 오류가 발생했습니다', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    // 에디터 오류 처리
    handleEditorError(error) {
      console.error('이미지 편집기 오류:', error);
      this.$bvToast.toast(`이미지 편집기 오류: ${error}`, {
        title: '오류',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 3000
      });

      // 오류 발생 시에도 에디터 닫기
      setTimeout(() => {
        this.resetEditor();
      }, 2000);
    },

    // 모든 이미지 AI 처리
    async processAllImages() {
      if (this.detailImages.length === 0) {
        this.$bvToast.toast('처리할 상세 이미지가 없습니다', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
        return;
      }

      try {
        // 처리 중 상태 설정
        this.isProcessing = true;
        this.processingMessage = '상세 이미지 처리 준비 중...';

        // 처리된 이미지 맵 - 기존의 것은 유지 (초기화 안 함)
        // 이미 처리된 이미지는 건너뛰기 위해 처리할 이미지 목록 생성
        const imagesToProcess = [];

        // 이미 처리된 이미지를 확인 (requestId 있는지 확인)
        for (let i = 0; i < this.detailImages.length; i++) {
          const imageUrl = this.detailImages[i];

          // 상단/하단 이미지인지 확인
          if (this.isTopOrBottomImage(imageUrl)) {
            console.log(`이미지 인덱스 ${i}는 상단/하단 이미지이므로 AI 처리 건너뜀:`, imageUrl.substring(0, 50) + '...');
            continue;
          }

          const existingProcessed = this.processedGroups.find(group =>
            group.index === i && group.requestId
          );

          if (existingProcessed) {
            console.log(`이미지 인덱스 ${i}는 이미 처리됨 (requestId: ${existingProcessed.requestId}). 건너뜀`);
          } else {
            imagesToProcess.push({
              url: imageUrl,
              index: i
            });
          }
        }

        if (imagesToProcess.length === 0) {
          this.$bvToast.toast('모든 이미지가 이미 처리되었습니다', {
            title: '알림',
            variant: 'info',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
          this.isProcessing = false;
          return;
        }

        this.processingMessage = `총 ${imagesToProcess.length}개 이미지 처리 시작...`;
        console.log(`처리할 이미지 ${imagesToProcess.length}개:`, imagesToProcess.map(img => img.index));

        // 이미지 처리 - 하나씩 처리
        const newlyProcessedImages = [];
        for (let i = 0; i < imagesToProcess.length; i++) {
          const imageInfo = imagesToProcess[i];

          this.processingMessage = `이미지 ${i+1}/${imagesToProcess.length} 처리 중...`;

          try {
            // 이미지 번역 처리
            const processedImage = await detailImageService.translateSingleImage(
              imageInfo.url,
              imageInfo.index,
              claimBasisOf(this.product)
            );

            if (processedImage) {
              // 처리된 이미지를 저장
              newlyProcessedImages.push(processedImage);

              // processedGroupsMap 업데이트
              this.$set(this.processedGroupsMap, imageInfo.index, processedImage);

              // 진행 상황 메시지 업데이트
              this.processingMessage = `이미지 ${i+1}/${imagesToProcess.length} 처리 완료...`;
            }
          } catch (error) {
            console.error(`이미지 인덱스 ${imageInfo.index} 처리 중 오류:`, error);
          }
        }

        // HTML 생성
        this.generateDetailHtmlFromProcessed();

        // 성공 메시지
        this.$bvToast.toast(`${newlyProcessedImages.length}개 이미지 처리가 완료되었습니다`, {
          title: '처리 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });

        // 최근 수정일 업데이트
        this.lastModifiedDate = new Date();
      } catch (error) {
        console.error('이미지 처리 오류:', error);
        this.$bvToast.toast(`이미지 처리 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 처리 상태 해제
        this.isProcessing = false;
        this.processingMessage = '';
      }
    },

    // 처리된 이미지로 HTML 생성
    // 이미지가 이미 처리되었는지 확인
    isImageProcessed(imageUrl) {
      // processedGroupsMap에서 원본 URL과 일치하는 항목 찾기
      return Object.values(this.processedGroupsMap).some(group =>
        group.originalUrls && group.originalUrls.includes(imageUrl)
      );
    },

    // 원본 이미지의 인덱스 찾기
    getOriginalImageIndex(imageUrl) {
      return this.detailImages.findIndex(url => url === imageUrl);
    },

    // 단일 이미지 번역 처리 (서비스 호출) - 중국어→한국어
    async translateSingleImage(imageUrl, index) {
      if (!imageUrl) {
        console.error('translateSingleImage: 이미지 URL이 없습니다');
        return;
      }

      // 상단/하단 이미지인 경우 처리하지 않음
      if (this.isTopOrBottomImage(imageUrl)) {
        console.log(`translateSingleImage: 인덱스 ${index}는 상단/하단 이미지이므로 AI 처리 건너뜀:`, imageUrl.substring(0, 50) + '...');
        return;
      }

      try {
        // 개별 이미지 로딩 상태 설정
        this.setImageLoading(index, true);

        console.log(`이미지 번역 시작 - 인덱스: ${index}, URL: ${imageUrl.substring(0, 30)}...`);

        // detailImageService의 translateSingleImage 메서드 호출
        const processedImage = await detailImageService.translateSingleImage(imageUrl, index, claimBasisOf(this.product));

        // 인덱스 재확인 - 원본 인덱스로 강제 설정
        // (서비스에서 유지되지 않았을 경우를 대비)
        processedImage.index = index;

        console.log(`번역 완료 - 결과 인덱스: ${processedImage.index}, 원본 인덱스: ${index}`);

        // 처리된 이미지를 Map에 저장 (인덱스를 키로 사용)
        // 이렇게 하면 정렬이 필요 없고 원래 위치를 유지함
        this.$set(this.processedGroupsMap, index, processedImage);

        console.log(`이미지를 인덱스 ${index}에 맵으로 저장함. 현재 맵:`, Object.keys(this.processedGroupsMap));

        // HTML 재생성
        this.generateDetailHtmlFromProcessed();

        // 번역한 이미지 위치로 스크롤 복원
        this.scrollToImage(index);

        // 번역 결과에 따른 성공 메시지 표시
        if (processedImage.ocrInfo && processedImage.ocrInfo.AITranslate) {
          this.$bvToast.toast('이미지 번역이 완료되었습니다 (AI 개선)', {
            title: '처리 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
        } else if (processedImage.ocrInfo) {
          this.$bvToast.toast('이미지 번역이 완료되었습니다 (OCR 정보 포함)', {
            title: '처리 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
        } else {
          this.$bvToast.toast('이미지 번역이 완료되었습니다', {
            title: '처리 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
        }

        // 최근 수정일 업데이트
        this.lastModifiedDate = new Date();

      } catch (error) {
        console.error('이미지 번역 오류:', error);

        this.$bvToast.toast(`이미지 번역 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 개별 이미지 로딩 상태 해제
        this.setImageLoading(index, false);
      }
    },

    // 단일 영어 이미지 번역 처리 - 영어→한국어
    async translateEnglishSingleImage(imageUrl, index) {
      if (!imageUrl) {
        console.error('translateEnglishSingleImage: 이미지 URL이 없습니다');
        return;
      }

      // 상단/하단 이미지인 경우 처리하지 않음
      if (this.isTopOrBottomImage(imageUrl)) {
        console.log(`translateEnglishSingleImage: 인덱스 ${index}는 상단/하단 이미지이므로 영어 번역 건너뜀:`, imageUrl.substring(0, 50) + '...');
        return;
      }

      try {
        // 개별 이미지 로딩 상태 설정
        this.setImageLoading(index, true);

        console.log(`영어 이미지 번역 시작 - 인덱스: ${index}, URL: ${imageUrl.substring(0, 30)}...`);

        // detailImageService의 translateEnglishImage 메서드 호출
        const processedImage = await detailImageService.translateEnglishImage(imageUrl, index);

        // 인덱스 재확인 - 원본 인덱스로 강제 설정
        processedImage.index = index;

        console.log(`영어 번역 완료 - 결과 인덱스: ${processedImage.index}, 원본 인덱스: ${index}`);

        // 처리된 이미지를 Map에 저장 (인덱스를 키로 사용)
        this.$set(this.processedGroupsMap, index, processedImage);

        console.log(`영어 번역 이미지를 인덱스 ${index}에 맵으로 저장함. 현재 맵:`, Object.keys(this.processedGroupsMap));

        // HTML 재생성
        this.generateDetailHtmlFromProcessed();

        // 번역한 이미지 위치로 스크롤 복원
        this.scrollToImage(index);

        // 성공 메시지
        this.$bvToast.toast('영어 이미지 번역이 완료되었습니다', {
          title: '처리 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });

        // 최근 수정일 업데이트
        this.lastModifiedDate = new Date();

      } catch (error) {
        console.error('영어 이미지 번역 오류:', error);

        this.$bvToast.toast(`영어 이미지 번역 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 개별 이미지 로딩 상태 해제
        this.setImageLoading(index, false);
      }
    },

    // 모든 영어 이미지 번역 처리
    async translateAllEnglishImages() {
      if (this.detailImages.length === 0) {
        this.$bvToast.toast('처리할 상세 이미지가 없습니다', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
        return;
      }

      try {
        // 처리 중 상태 설정
        this.isProcessing = true;
        this.processingMessage = '영어 상세 이미지 처리 준비 중...';

        // 처리할 이미지 목록 생성 — 화면에 보이는 낱장 기준(작업 끝난 건은 저장 목록이 합본 1장뿐이다)
        const imagesToProcess = englishTranslateTargets(
          this.processedGroups,
          this.detailImages,
          url => this.isTopOrBottomImage(url)
        );

        if (imagesToProcess.length === 0) {
          this.$bvToast.toast('영어 번역할 이미지가 없습니다', {
            title: '알림',
            variant: 'info',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
          this.isProcessing = false;
          return;
        }

        this.processingMessage = `총 ${imagesToProcess.length}개 영어 이미지 처리 시작...`;
        console.log(`영어 번역할 이미지 ${imagesToProcess.length}개:`, imagesToProcess.map(img => img.index));

        // 이미지 처리 - 하나씩 처리
        const newlyProcessedImages = [];
        for (let i = 0; i < imagesToProcess.length; i++) {
          const imageInfo = imagesToProcess[i];

          this.processingMessage = `영어 이미지 ${i+1}/${imagesToProcess.length} 처리 중...`;

          try {
            // 영어 이미지 번역 처리
            const processedImage = await detailImageService.translateEnglishImage(
              imageInfo.url,
              imageInfo.index
            );

            // 영어 글자가 없어 번역 결과가 없으면 기존 사진(번역·편집 정보 포함)을 그대로 둔다
            if (processedImage && !processedImage.translationFailed) {
              // 처리된 이미지를 저장
              newlyProcessedImages.push(processedImage);

              // processedGroupsMap 업데이트
              this.$set(this.processedGroupsMap, imageInfo.index, processedImage);

              // 진행 상황 메시지 업데이트
              this.processingMessage = `영어 이미지 ${i+1}/${imagesToProcess.length} 처리 완료...`;
            }
          } catch (error) {
            console.error(`영어 이미지 인덱스 ${imageInfo.index} 처리 중 오류:`, error);
          }
        }

        // HTML 생성
        this.generateDetailHtmlFromProcessed();

        // 성공 메시지
        this.$bvToast.toast(`${newlyProcessedImages.length}개 영어 이미지 번역이 완료되었습니다`, {
          title: '처리 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });

        // 최근 수정일 업데이트
        this.lastModifiedDate = new Date();
      } catch (error) {
        console.error('영어 이미지 처리 오류:', error);
        this.$bvToast.toast(`영어 이미지 처리 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 처리 상태 해제
        this.isProcessing = false;
        this.processingMessage = '';
      }
    },

    // 이미 처리된 이미지 재번역
    async retranslateImage(index) {
      console.log(`retranslateImage 호출됨, 인덱스: ${index}, 맵 키: ${Object.keys(this.processedGroupsMap)}`);

      // 배열 인덱스가 아닌 그룹 인덱스로 그룹 검색
      const group = this.processedGroupsMap[index];
      if (!group) {
        console.error('retranslateImage: 인덱스에 해당하는 이미지 그룹이 없음', index);
        return;
      }

      // 원본 인덱스 저장 (스코프 문제 해결을 위해 try 밖으로 이동)
      const originalIndex = group.index;
      console.log(`재번역 시작, 배열 인덱스: ${index}, 원본 인덱스: ${originalIndex}`);

      try {
        // 개별 이미지 로딩 상태 설정
        this.setImageLoading(originalIndex, true);

        // detailImageService의 retranslateImage 메서드 호출
        const processedImage = await detailImageService.retranslateImage(group, claimBasisOf(this.product));

        // 중요: 원본 인덱스를 유지해야 함
        processedImage.index = originalIndex;

        // 처리된 이미지를 Map에 저장 (인덱스를 키로 사용하여 위치 보존)
        this.$set(this.processedGroupsMap, originalIndex, processedImage);

        console.log(`재번역된 이미지를 인덱스 ${originalIndex}에 맵으로 저장함`);

        // HTML 재생성
        this.generateDetailHtmlFromProcessed();

        // 재번역한 이미지 위치로 스크롤 복원
        this.scrollToImage(originalIndex);

        // 성공 메시지
        this.$bvToast.toast('이미지 재번역이 완료되었습니다', {
          title: '처리 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });

        // 최근 수정일 업데이트
        this.lastModifiedDate = new Date();

      } catch (error) {
        console.error('이미지 재번역 오류:', error);
        this.$bvToast.toast(`이미지 재번역 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 개별 이미지 로딩 상태 해제
        this.setImageLoading(originalIndex, false);
      }
    },

    // 이미지 위로 이동
    moveImageUp(index) {
      try {
        console.log(`moveImageUp 호출됨, 인덱스: ${index}, 맵 키: ${Object.keys(this.processedGroupsMap)}`);

        // 원본 인덱스로 이미지를 찾음 (processedGroups 배열 인덱스가 아님)
        const groupIndex = this.processedGroups.findIndex(g => g.index === index);
        if (groupIndex <= 0 || groupIndex >= this.processedGroups.length) {
          console.log('이미지 위로 이동: 인덱스 범위 오류', {index, groupIndex});
          return;
        }

        // 현재 이미지와 이전 이미지 가져오기
        const currentImage = this.processedGroups[groupIndex];
        const prevImage = this.processedGroups[groupIndex - 1];

        if (!currentImage || !prevImage) {
          console.error('이미지 위로 이동: 이미지 객체가 유효하지 않음', {
            currentImage: !!currentImage,
            prevImage: !!prevImage
          });
          return;
        }

        console.log(`이미지 위로 이동: 인덱스 ${index} (배열 위치: ${groupIndex})에서 위치 ${prevImage.index}로 이동`);

        // 두 이미지의 원본 인덱스 저장
        const curIndex = currentImage.index;
        const prevIndex = prevImage.index;

        // 맵에서 이미지를 교환
        const tempImage = {...currentImage};
        tempImage.index = prevIndex;  // 인덱스를 이전 이미지의 인덱스로 변경

        const tempPrevImage = {...prevImage};
        tempPrevImage.index = curIndex;  // 이전 이미지의 인덱스를 현재 이미지의 인덱스로 변경

        // 맵 업데이트
        this.$set(this.processedGroupsMap, prevIndex, tempImage);
        this.$set(this.processedGroupsMap, curIndex, tempPrevImage);

        // HTML 재생성
        this.generateDetailHtmlFromProcessed();

        // 알림
        this.$bvToast.toast(`이미지가 위로 이동되었습니다`, {
          title: '이미지 위치 변경',
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500
        });
      } catch (error) {
        console.error('이미지 위로 이동 중 오류 발생:', error);
        this.$bvToast.toast('이미지 이동 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    // 이미지 아래로 이동
    moveImageDown(index) {
      try {
        console.log(`moveImageDown 호출됨, 인덱스: ${index}, 맵 키: ${Object.keys(this.processedGroupsMap)}`);

        // 원본 인덱스로 이미지를 찾음 (processedGroups 배열 인덱스가 아님)
        const groupIndex = this.processedGroups.findIndex(g => g.index === index);
        if (groupIndex < 0 || groupIndex >= this.processedGroups.length - 1) {
          console.log('이미지 아래로 이동: 인덱스 범위 오류', {index, groupIndex});
          return;
        }

        // 현재 이미지와 다음 이미지 가져오기
        const currentImage = this.processedGroups[groupIndex];
        const nextImage = this.processedGroups[groupIndex + 1];

        if (!currentImage || !nextImage) {
          console.error('이미지 아래로 이동: 이미지 객체가 유효하지 않음', {
            currentImage: !!currentImage,
            nextImage: !!nextImage
          });
          return;
        }

        console.log(`이미지 아래로 이동: 인덱스 ${index} (배열 위치: ${groupIndex})에서 위치 ${nextImage.index}로 이동`);

        // 두 이미지의 원본 인덱스 저장
        const curIndex = currentImage.index;
        const nextIndex = nextImage.index;

        // 맵에서 이미지를 교환
        const tempImage = {...currentImage};
        tempImage.index = nextIndex;  // 인덱스를 다음 이미지의 인덱스로 변경

        const tempNextImage = {...nextImage};
        tempNextImage.index = curIndex;  // 다음 이미지의 인덱스를 현재 이미지의 인덱스로 변경

        // 맵 업데이트
        this.$set(this.processedGroupsMap, nextIndex, tempImage);
        this.$set(this.processedGroupsMap, curIndex, tempNextImage);

        // HTML 재생성
        this.generateDetailHtmlFromProcessed();

        // 알림
        this.$bvToast.toast(`이미지가 아래로 이동되었습니다`, {
          title: '이미지 위치 변경',
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500
        });
      } catch (error) {
        console.error('이미지 아래로 이동 중 오류 발생:', error);
        this.$bvToast.toast('이미지 이동 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    // 처리된 이미지 삭제
    async deleteImage(index) {
      try {
        console.log(`deleteImage 호출됨, 인덱스: ${index}, 맵 키: ${Object.keys(this.processedGroupsMap)}`);

        // 이미지가 맵에 존재하는지 확인
        if (!this.processedGroupsMap[index]) {
          console.log('deleteImage: 맵에 해당 인덱스의 이미지가 없음', index);
          return;
        }

        // 삭제 전 이미지 정보 로깅
        const imageToDelete = this.processedGroupsMap[index];
        console.log('이미지 삭제 요청:', {
          인덱스: index,
          이미지그룹인덱스: imageToDelete?.index || '알 수 없음',
          URL: imageToDelete?.url ? (imageToDelete.url.substring(0, 30) + '...') : '없음'
        });

        // 브라우저 기본 confirm 은 탭 JS 를 멈춰 외부 자동화(CDP)가 시간 초과된다 → 화면 안 모달 사용(확인 버튼에 포커스라 Enter 로 바로 확인)
        const confirmed = await this.$bvModal.msgBoxConfirm('이 이미지를 삭제하시겠습니까?', {
          title: '확인',
          okTitle: '삭제',
          okVariant: 'danger',
          cancelTitle: '취소',
          autoFocusButton: 'ok',
          centered: true
        });
        if (!confirmed) {
          console.log('이미지 삭제 취소됨');
          return;
        }

        try {
          // 이미지가 여전히 맵에 존재하는지 다시 확인
          if (!this.processedGroupsMap[index]) {
            console.error('deleteImage: 확인 후 삭제 시도 중 맵에 해당 이미지가 없음', index);
            throw new Error('삭제할 이미지가 더 이상 존재하지 않습니다');
          }

          // 원본 이미지 배열에서도 제거
          const updatedProduct = { ...this.product };
          if (updatedProduct.detailImages && updatedProduct.detailImages[index]) {
            console.log(`원본 이미지 배열에서 인덱스 ${index} 제거`);
            const updatedDetailImages = [...updatedProduct.detailImages];
            updatedDetailImages.splice(index, 1);
            updatedProduct.detailImages = updatedDetailImages;

            // 맵에서 이미지 삭제
            this.$delete(this.processedGroupsMap, index);

            // 맵의 키 재정렬 (나머지 이미지들의 인덱스를 재조정)
            const newProcessedGroupsMap = {};
            const oldKeys = Object.keys(this.processedGroupsMap).sort((a, b) => parseInt(a) - parseInt(b));

            console.log(`인덱스 재정렬 시작 - 삭제된 인덱스: ${index}, 키 목록:`, oldKeys);

            // 삭제된 인덱스보다 큰 모든 인덱스를 한 칸씩 당김
            oldKeys.forEach(oldKey => {
              const oldKeyNum = parseInt(oldKey);
              if (oldKeyNum > index) {
                // 인덱스가 더 큰 경우 한 칸 앞으로 이동
                const newKeyNum = oldKeyNum - 1;
                const image = { ...this.processedGroupsMap[oldKey] };
                image.index = newKeyNum; // 이미지 내부의 인덱스도 업데이트
                newProcessedGroupsMap[newKeyNum] = image;
                console.log(`이미지 인덱스 조정: ${oldKeyNum} -> ${newKeyNum}`);
              } else {
                // 인덱스가 더 작은 경우 그대로 유지
                newProcessedGroupsMap[oldKey] = { ...this.processedGroupsMap[oldKey] };
              }
            });

            // 새 맵으로 교체
            this.processedGroupsMap = {};
            Object.keys(newProcessedGroupsMap).forEach(key => {
              this.$set(this.processedGroupsMap, key, newProcessedGroupsMap[key]);
            });

            console.log('인덱스 재정렬 완료, 새 키 목록:', Object.keys(this.processedGroupsMap).sort((a, b) => parseInt(a) - parseInt(b)));

            // 로컬 상태 업데이트 (UI 즉시 반영)
            this.$store.commit('UPDATE_ROCKET_PRODUCT', updatedProduct);
          } else {
            console.log(`원본 이미지 배열에서 인덱스 ${index}를 찾을 수 없거나 이미 제거됨`);
            // 맵에서만 삭제
            this.$delete(this.processedGroupsMap, index);
          }

          // HTML은 삭제된 이미지를 제외하고 재생성
          this.generateDetailHtmlFromProcessed();

          // 알림
          this.$bvToast.toast('이미지가 삭제되었습니다', {
            title: '이미지 삭제',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 1500
          });

          console.log('이미지 삭제 완료, 남은 이미지 수:', this.processedGroups.length);
        } catch (innerError) {
          console.error('이미지 삭제 처리 중 오류:', innerError);
          this.$bvToast.toast(`이미지 삭제 중 오류가 발생했습니다: ${innerError.message}`, {
            title: '오류',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 3000
          });
        }
      } catch (error) {
        console.error('이미지 삭제 초기화 오류:', error);
        this.$bvToast.toast(`이미지 삭제 초기화 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    // 원본 이미지 위로 이동
    moveOriginalImageUp(index) {
      try {
        console.log('moveOriginalImageUp 호출됨, 인덱스:', index);

        if (index <= 0 || index >= this.detailImages.length) {
          console.log('원본 이미지 위로 이동: 인덱스 범위 오류', index);
          return;
        }

        // 제품 정보 복사
        const updatedProduct = { ...this.product };
        const detailImages = [...updatedProduct.detailImages];

        console.log('이미지 이동 전:', detailImages.map((url, i) => `${i}: ${url.substring(0, 30)}...`));

        // 현재 이미지와 이전 이미지 위치 교환
        const temp = detailImages[index];
        detailImages[index] = detailImages[index - 1];
        detailImages[index - 1] = temp;

        console.log('이미지 이동 후:', detailImages.map((url, i) => `${i}: ${url.substring(0, 30)}...`));

        // 제품 정보 업데이트
        updatedProduct.detailImages = detailImages;
        this.updateRocketProduct(updatedProduct);

        // HTML 재생성
        this.initializeDetailHtml();

        // 알림
        this.$bvToast.toast('이미지가 위로 이동되었습니다', {
          title: '이미지 위치 변경',
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500
        });
      } catch (error) {
        console.error('원본 이미지 위로 이동 중 오류:', error);
        this.$bvToast.toast('이미지 이동 중 오류가 발생했습니다', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    // 원본 이미지 아래로 이동
    moveOriginalImageDown(index) {
      try {
        console.log('moveOriginalImageDown 호출됨, 인덱스:', index);

        if (index < 0 || index >= this.detailImages.length - 1) {
          console.log('원본 이미지 아래로 이동: 인덱스 범위 오류', index);
          return;
        }

        // 제품 정보 복사
        const updatedProduct = { ...this.product };
        const detailImages = [...updatedProduct.detailImages];

        console.log('이미지 이동 전:', detailImages.map((url, i) => `${i}: ${url.substring(0, 30)}...`));

        // 현재 이미지와 다음 이미지 위치 교환
        const temp = detailImages[index];
        detailImages[index] = detailImages[index + 1];
        detailImages[index + 1] = temp;

        console.log('이미지 이동 후:', detailImages.map((url, i) => `${i}: ${url.substring(0, 30)}...`));

        // 제품 정보 업데이트
        updatedProduct.detailImages = detailImages;
        this.updateRocketProduct(updatedProduct);

        // HTML 재생성
        this.initializeDetailHtml();

        // 알림
        this.$bvToast.toast('이미지가 아래로 이동되었습니다', {
          title: '이미지 위치 변경',
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500
        });
      } catch (error) {
        console.error('원본 이미지 아래로 이동 중 오류:', error);
        this.$bvToast.toast('이미지 이동 중 오류가 발생했습니다', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    // 원본 이미지 삭제
    async deleteOriginalImage(index) {
      if (index < 0 || index >= this.detailImages.length) return;

      console.log(`deleteOriginalImage 호출됨, 인덱스: ${index}, 맵 키: ${Object.keys(this.processedGroupsMap).sort()}`);

      // 현재 이미지가 상단/하단 이미지인지 확인
      const currentImageUrl = this.detailImages[index];
      if (this.isTopOrBottomImage(currentImageUrl)) {
        console.log('상단/하단 이미지는 삭제할 수 없습니다:', currentImageUrl.substring(0, 50) + '...');
        this.$bvToast.toast('설정에서 업로드된 이미지는 삭제할 수 없습니다', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
        return;
      }

      // 브라우저 기본 confirm 은 탭 JS 를 멈춰 외부 자동화(CDP)가 시간 초과된다 → 화면 안 모달 사용(확인 버튼에 포커스라 Enter 로 바로 확인)
      const confirmed = await this.$bvModal.msgBoxConfirm('이 원본 이미지를 삭제하시겠습니까?', {
        title: '확인',
        okTitle: '삭제',
        okVariant: 'danger',
        cancelTitle: '취소',
        autoFocusButton: 'ok',
        centered: true
      });
      if (!confirmed) {
        return;
      }

      try {
        // 제품 정보 복사
        const updatedProduct = { ...this.product };

        // 상세 이미지 배열에서 해당 이미지 제거
        const updatedDetailImages = [...updatedProduct.detailImages];
        updatedDetailImages.splice(index, 1);
        updatedProduct.detailImages = updatedDetailImages;

        // 맵의 키가 있는 경우, 맵도 재정렬 (삭제된 인덱스 이후의 키들을 한 칸씩 당김)
        if (Object.keys(this.processedGroupsMap).length > 0) {
          console.log('processedGroupsMap 키 재조정 시작');

          // 1. 기존 맵에서 삭제할 인덱스 제거
          if (this.processedGroupsMap[index]) {
            this.$delete(this.processedGroupsMap, index);
            console.log(`인덱스 ${index}의 이미지를 맵에서 제거함`);
          }

          // 2. 나머지 맵의 키 재정렬 (인덱스 이동)
          const newProcessedGroupsMap = {};
          const oldKeys = Object.keys(this.processedGroupsMap).sort((a, b) => parseInt(a) - parseInt(b));

          console.log(`인덱스 재정렬 시작 - 삭제된 인덱스: ${index}, 키 목록:`, oldKeys);

          // 3. 삭제된 인덱스보다 큰 모든 인덱스를 한 칸씩 당김
          oldKeys.forEach(oldKey => {
            const oldKeyNum = parseInt(oldKey);
            if (oldKeyNum > index) {
              // 인덱스가 더 큰 경우 한 칸 앞으로 이동
              const newKeyNum = oldKeyNum - 1;
              const image = { ...this.processedGroupsMap[oldKey] };
              image.index = newKeyNum; // 이미지 내부의 인덱스도 업데이트
              newProcessedGroupsMap[newKeyNum] = image;
              console.log(`이미지 인덱스 조정: ${oldKeyNum} -> ${newKeyNum}`);
            } else {
              // 인덱스가 더 작은 경우 그대로 유지
              newProcessedGroupsMap[oldKey] = { ...this.processedGroupsMap[oldKey] };
            }
          });

          // 4. 새 맵으로 교체
          this.processedGroupsMap = {};
          Object.keys(newProcessedGroupsMap).forEach(key => {
            this.$set(this.processedGroupsMap, key, newProcessedGroupsMap[key]);
          });

          console.log('인덱스 재정렬 완료, 새 키 목록:', Object.keys(this.processedGroupsMap).sort((a, b) => parseInt(a) - parseInt(b)));
        }

        // Vuex 상태 업데이트
        await this.updateRocketProduct(updatedProduct);

        // HTML 재생성
        this.initializeDetailHtml();

        // 알림
        this.$bvToast.toast('원본 이미지가 삭제되었습니다', {
          title: '이미지 삭제',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500
        });
      } catch (error) {
        console.error('원본 이미지 삭제 중 오류:', error);
        this.$bvToast.toast(`이미지 삭제 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      }
    },

    generateDetailHtmlFromProcessed() {
      if (this.processedGroups.length === 0) {
        return;
      }

      let html = `
      <div style="width:100%; max-width:800px; margin:0 auto; background-color:#ffffff; padding:20px;">
      `;

      // 이미지를 타입별로 분류
      const topImage = this.processedGroups.find(group => group.isTopImage);
      const bottomImage = this.processedGroups.find(group => group.isBottomImage);

      // 일반 이미지 (상단/하단 이미지 제외)
      const contentImages = this.processedGroups
        .filter(group => !group.isSettingImage) // 설정 이미지(상단/하단) 제외
        .sort((a, b) => a.index - b.index);  // 인덱스 순으로 정렬

      console.log(`HTML 생성: 상단이미지=${!!topImage}, 하단이미지=${!!bottomImage}, 컨텐츠이미지=${contentImages.length}개`);

      // 상단 이미지 추가 (있는 경우)
      if (topImage) {
        html += `
        <div style="margin-bottom:${this.imageMargin}px; text-align:center;">
          <img src="${topImage.url}" alt="상단 이미지" style="max-width:100%; height:auto; width:760px; display:block; margin:0 auto;">
        </div>
        `;
      }

      // 컨텐츠 이미지 추가 (번역된 이미지 또는 원본 이미지)
      contentImages.forEach((group, i) => {
        html += `
        <div id="detail-image-${group.index}" style="margin-bottom:${this.imageMargin}px; text-align:center;">
          <img src="${group.url}" alt="상세 이미지 ${i + 1}" style="max-width:100%; height:auto; width:760px; display:block; margin:0 auto;">
        </div>
        `;
      });

      // 하단 이미지 추가 (있는 경우)
      if (bottomImage) {
        html += `
        <div style="margin-bottom:${this.imageMargin}px; text-align:center;">
          <img src="${bottomImage.url}" alt="하단 이미지" style="max-width:100%; height:auto; width:760px; display:block; margin:0 auto;">
        </div>
        `;
      }

      html += '</div>';
      this.detailHtml = html;

      console.log('HTML 생성 완료, 길이:', html.length);
    },

    // 초기화 기능 (전체 편집기 내용)
    initializeAllContent() {
      this.$bvModal.msgBoxConfirm('모든 편집 내용이 초기화됩니다. 계속하시겠습니까?', {
        title: '초기화 확인',
        okVariant: 'danger',
        okTitle: '초기화',
        cancelTitle: '취소',
        hideHeaderClose: false,
        centered: true
      }).then(confirmed => {
        if (confirmed) {
          this.processedGroups = [];
          this.selectedOriginalIndex = -1;
          this.selectedProcessedIndex = -1;
          this.resetEditor(); // 사용: 편집기 상태 초기화 메서드 호출
          this.initializeDetailHtml();

          this.$bvToast.toast('편집 내용이 초기화되었습니다', {
            title: '초기화 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 1500
          });
        }
      });
    },

    // 상단/하단 이미지인지 확인하는 메서드
    isTopOrBottomImage(imageUrl) {
      if (!imageUrl) return false;

      // 1. URL 직접 비교 (기존 방식)
      const settings = this.defaultSettings.imageSettings || {};
      const topImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
      const bottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

      if (imageUrl === topImageUrl || imageUrl === bottomImageUrl) {
        return true;
      }

      // 2. 파일명에 topimage 또는 bottomimage 포함 여부 확인 (추가 방식)
      try {
        // URL에서 파일명 추출
        const urlObj = new URL(imageUrl);
        const pathname = urlObj.pathname;
        const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

        // 파일명에 topimage 또는 bottomimage 포함 여부 확인
        return filename.includes('topimage') || filename.includes('bottomimage');
      } catch (error) {
        // URL 파싱 오류 시 기존 방식 결과 반환
        return false;
      }
    },

    // 이미지 업로드 다이얼로그 표시
    showImageUpload() {
      this.$refs.imageUploadInput.click();
    },

    // 이미지 업로드 처리
    async handleImageUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      try {
        // 로딩 상태 설정
        this.setProcessing({
          isProcessing: true,
          message: '이미지 업로드 중...'
        });
        this.isProcessing = true;

        // 기존 제품 정보 복사
        const updatedProduct = { ...this.product };
        const detailImages = [...(updatedProduct.detailImages || [])];

        // 업로드된 이미지 인덱스 추적
        const uploadedIndices = [];

        // 각 파일 업로드
        for (const file of files) {
          // 이미지 파일만 허용
          if (!file.type.startsWith('image/')) {
            continue;
          }

          // 파일을 Base64로 변환
          const base64Image = await imageService.fileToBase64(file);

          // S3에 업로드
          const supplierCode = this.$store.state.user.suppliercode || this.$store.state.user.companyid;
          const fileName = `${supplierCode}_${updatedProduct.registrationId}_detail_${Date.now()}_${detailImages.length + 1}.jpg`;

          var imageResult = await imageService.uploadImageToS3(
            base64Image,
            fileName,
            'detail'
          );

          const imageUrl = imageResult.imageUrl;

          // 업로드된 이미지 URL을 상세 이미지 배열에 추가
          if (imageUrl) {
            // 현재 인덱스 저장
            const currentIndex = detailImages.length;
            uploadedIndices.push(currentIndex);

            // 이미지 URL 추가
            detailImages.push(imageUrl);

            // processedGroupsMap에 원본 이미지 정보 추가
            this.$set(this.processedGroupsMap, currentIndex, {
              index: currentIndex,
              url: imageUrl,
              originalUrls: [imageUrl],
              processType: 'original',
              isProcessed: false
            });

            console.log(`이미지 업로드 완료: 인덱스 ${currentIndex}, URL: ${imageUrl.substring(0, 50)}...`);
          }
        }

        // 제품 정보 업데이트
        updatedProduct.detailImages = detailImages;

        // Vuex 상태 업데이트
        await this.updateRocketProduct(updatedProduct);

        // HTML 재생성 (새 이미지 포함)
        this.initializeDetailHtml();

        // 처리된 이미지 기반 HTML 생성 (processedGroupsMap 반영)
        this.generateDetailHtmlFromProcessed();

        // 성공 메시지
        this.$bvToast.toast(`${files.length}개 이미지가 업로드되었습니다`, {
          title: '업로드 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });

        // 업로드된 이미지 중 첫 번째 이미지 선택 (있는 경우)
        if (uploadedIndices.length > 0) {
          this.selectOriginalGroup(uploadedIndices[0]);
        }
      } catch (error) {
        console.error('이미지 업로드 오류:', error);
        this.$bvToast.toast(`이미지 업로드 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 로딩 상태 해제
        this.setProcessing({
          isProcessing: false,
          message: ''
        });
        this.isProcessing = false;

        // 파일 입력 초기화
        event.target.value = '';
      }
    },

    // 편집 모드로 전환
    switchToEditMode() {
      this.isPreviewMode = false;
    },

    // 미리보기 모드로 전환
    switchToPreviewMode() {
      this.isPreviewMode = true;
    },

    // 저장 기능
    async saveDetailPage() {
      try {
        // 처리 중 상태 설정
        this.setProcessing({
          isProcessing: true,
          message: '상세 페이지 저장 중...'
        });
        this.isProcessing = true;

        // 처리된 이미지가 없는 경우 경고
        if (this.processedGroups.length === 0) {
          this.$bvToast.toast('처리된 이미지가 없습니다. AI 자동 생성을 먼저 실행해주세요.', {
            title: '알림',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });

          this.setProcessing({ isProcessing: false });
          this.isProcessing = false;
          return;
        }

        // 기존 제품 정보 복사
        const updatedProduct = { ...this.product };

        // 제품 정보 업데이트 - 번역된 이미지 URL 그대로 사용
        updatedProduct.detailHtml = this.detailHtml;

        // 상단/하단 이미지를 제외한 처리된 이미지만 저장
        const filteredProcessedGroups = this.processedGroups.filter(group => {
          // 이미지 URL이 없는 경우 제외
          if (!group || !group.url) return false;

          // 설정 이미지(상단/하단)인 경우 제외
          return !group.isSettingImage;
        });

        console.log(`처리된 이미지 필터링: 전체 ${this.processedGroups.length}개 중 ${filteredProcessedGroups.length}개 남음 (상단/하단 이미지 제외)`);

        updatedProduct.processedDetailImages = [...filteredProcessedGroups]; // 깊은 복사
        // 편집 화면에서 다시 번역한 칸도 경고에 반영한다(목록 아이콘·작업 결과가 같은 값을 본다)
        updatedProduct.detailClaimWarning = buildClaimWarning(filteredProcessedGroups);

        // 상세 이미지 URL 목록 생성 - 번역된 이미지 URL을 그대로 사용
        const detailImageUrls = this.processedGroups
          .filter(group => !group.url.startsWith('data:')) // base64 데이터는 제외
          .map(group => group.url); // URL만 추출

        console.log('저장 시 사용할 이미지 URLs:', detailImageUrls);

        // base64 이미지가 있는 경우만 S3에 업로드 (필요한 경우에만)
        const base64Images = this.processedGroups.filter(group =>
          group.url.startsWith('data:')
        );

        if (base64Images.length > 0) {
          this.setProcessing({
            isProcessing: true,
            message: 'base64 이미지 업로드 중...'
          });

          // 각 이미지 업로드 (필요한 경우에만)
          for (const group of base64Images) {
            const supplierCode = this.$store.state.user.suppliercode || this.$store.state.user.companyid;
            const fileName = `${supplierCode}_${updatedProduct.registrationId}_detail_group_${group.index}.jpg`;

            try {
              // uploadImageToS3 는 { imageUrl, success } 를 돌려준다(같은 파일 저장 길과 같게 .imageUrl 로 읽는다 —
              // 객체째 쓰면 상세 주소가 «[object Object]» 로 저장됐다)
              const uploaded = await imageService.uploadImageToS3(
                group.url,
                fileName,
                'detail'
              );
              const imageUrl = uploaded && uploaded.imageUrl;
              if (!imageUrl) throw new Error('S3 업로드 결과에 이미지 주소가 없어요');

              // URL 업데이트
              if (imageUrl) {
                // 그룹 URL 업데이트
                const groupIndex = updatedProduct.processedDetailImages.findIndex(g => g.index === group.index);
                if (groupIndex >= 0) {
                  updatedProduct.processedDetailImages[groupIndex].url = imageUrl;
                }

                // 이미지 목록에 추가
                detailImageUrls.push(imageUrl);

                // HTML에서도 이미지 URL 업데이트
                updatedProduct.detailHtml = updatedProduct.detailHtml.replace(
                  new RegExp(group.url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
                  imageUrl
                );
              }
            } catch (error) {
              // 넘어가면 이 그림이 빠진 채 «완료» 로 저장된다 — 저장을 멈추고 까닭을 보인다(상태는 그대로 · 다시 저장)
              console.error(`이미지 업로드 오류 (그룹 ${group.index}):`, error);
              throw new Error(`상세 이미지 ${group.index + 1}번을 올리지 못해 저장을 멈췄어요: ${this.serverReason(error)} — 다시 저장해 주세요.`);
            }
          }
        }

        // 상세 이미지 배열 업데이트
        updatedProduct.detailImages = detailImageUrls;

        // detailImageStatus 업데이트 - detailImageService와 동일한 방식으로 설정
        // 문자열 형태로 설정 (AIRocketReg.vue의 badge 표시에 중요)
        updatedProduct.detailImageStatus = 'completed';

        // 객체 구조도 유지 (다른 코드가 이 구조를 사용할 수 있음)
        updatedProduct.detailImageStatusObj = {
          status: 'completed',
          images: [...detailImageUrls]
        };

        updatedProduct.lastModified = new Date();

        // coupangDoc용 통합 이미지 생성 시작
        this.setProcessing({
          isProcessing: true,
          message: 'coupangDoc용 통합 이미지 생성 중...'
        });

        // 설정 정보는 이제 필요하지 않음 (topImage와 bottomImage를 직접 찾아서 사용)

        // 통합 이미지용 HTML 생성
        let combinedHtml = `
          <div style="width:800px; margin:0; padding:0; background-color:#ffffff; box-sizing:border-box;">
        `;

        // 이미지를 타입별로 분류
        const topImage = this.processedGroups.find(group => group.isTopImage);
        const bottomImage = this.processedGroups.find(group => group.isBottomImage);

        // 일반 이미지 (상단/하단 이미지 제외)
        const contentImages = this.processedGroups
          .filter(group => !group.isSettingImage) // 설정 이미지(상단/하단) 제외
          .sort((a, b) => a.index - b.index);  // 인덱스 순으로 정렬

        console.log(`통합 이미지 HTML 생성: 상단이미지=${!!topImage}, 하단이미지=${!!bottomImage}, 컨텐츠이미지=${contentImages.length}개`);

        // 상단 이미지 추가 (있는 경우)
        if (topImage) {
          console.log('통합 이미지에 상단 이미지 추가');
          combinedHtml += `
          <div style="margin:0; padding:0; text-align:center; width:800px;">
            <img src="${topImage.url}" alt="상단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
          </div>
          `;
        }

        // 컨텐츠 이미지 추가 (번역된 이미지 또는 원본 이미지)
        contentImages.forEach((group, index) => {
          if (group.url) {
            console.log(`통합 이미지에 컨텐츠 이미지 ${index+1} 추가`);
            combinedHtml += `
            <div style="margin:0; padding:0; text-align:center; width:800px;">
              <img src="${group.url}" alt="상세 이미지 ${index+1}"
                  style="width:800px; display:block; margin:0 auto; padding:0;"
                  width="800">
            </div>
            `;
          }
        });

        // 하단 이미지 추가 (있는 경우)
        if (bottomImage) {
          console.log('통합 이미지에 하단 이미지 추가');
          combinedHtml += `
          <div style="margin:0; padding:0; text-align:center; width:800px;">
            <img src="${bottomImage.url}" alt="하단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
          </div>
          `;
        }

        combinedHtml += '</div>';

        // HTML을 단일 이미지로 변환 (S3에 업로드)
        console.log('HTML을 단일 이미지로 변환 시작');
        let combinedImageUrl = '';
        // 통합 한 장을 못 만들면 개별 이미지 주소를 쉼표로 잇는다 — «작업 개시»(detailImageService processDetailImages)가
        // 늘 쓰는 꼴과 같다. 다만 사람에게는 성공이 아니라 경고로 알린다(까닭 원문).
        let combinedFailReason = '';
        try {
          // throwOnError — 실패를 null 로 삼키면 까닭(서버 응답)이 사라진다
          combinedImageUrl = await detailImageService.convertHtmlToImage(combinedHtml, updatedProduct.registrationId, { throwOnError: true });
          console.log('coupangDoc용 통합 이미지 생성 완료:', combinedImageUrl.substring(0, 50) + '...');
        } catch (error) {
          console.error('통합 이미지 생성 오류:', error);
          combinedFailReason = this.serverReason(error);
          combinedImageUrl = detailImageUrls.join(',');
          console.error('통합 이미지 생성 실패, 콤마로 구분된 URL 사용:', combinedImageUrl.substring(0, 50) + '...');
        }

        // coupangDoc 업데이트 - 통합된 이미지 URL 설정
        if (updatedProduct.coupangDoc && updatedProduct.coupangDoc.jsonDocument &&
            updatedProduct.coupangDoc.jsonDocument.length > 0) {
          const docObj = updatedProduct.coupangDoc.jsonDocument[0];
          if (docObj.imagePage && docObj.imagePage.details) {
            if (combinedImageUrl) {
              // 통합된 이미지 URL 설정
              docObj.imagePage.details.detailedImage = combinedImageUrl;
              docObj.imagePage.details.htmlProductDetailContent = "";
              console.log(`상품 coupangDoc.detailedImage 설정: ${combinedImageUrl.substring(0, 50)}...`);
            } else if (detailImageUrls && detailImageUrls.length > 0) {
              // 통합 이미지 생성 실패 시 콤마로 구분된 URL로 폴백
              const fallbackUrl = detailImageUrls.join(',');
              docObj.imagePage.details.detailedImage = fallbackUrl;
              docObj.imagePage.details.htmlProductDetailContent = "";
              console.log(`통합 이미지 생성 실패, 콤마로 구분된 URL 사용: ${fallbackUrl.substring(0, 50)}...`);
            }

            // altText 설정 — 원제목(1688 중국어)은 안 씀 · 비었거나 한자가 들었으면 다시 고름(detailAltText)
            if (needsDetailAltText(docObj.imagePage.details.altText)) {
              docObj.imagePage.details.altText = detailAltText(updatedProduct);
            }
          }
        }

        // 옵션이 있는 경우 각 옵션의 detailImage 및 coupangDoc도 업데이트
        if (updatedProduct.options && updatedProduct.options.length > 0) {
          updatedProduct.options.forEach(option => {
            // 옵션의 detailImages 및 status 업데이트
            option.detailImages = [...detailImageUrls];

            // 옵션의 detailImageStatus 업데이트 - detailImageService와 동일한 방식으로 설정
            // 문자열 형태로 설정 (AIRocketReg.vue의 badge 표시에 중요)
            option.detailImageStatus = 'completed';

            // 객체 구조도 유지 (다른 코드가 이 구조를 사용할 수 있음)
            option.detailImageStatusObj = {
              status: 'completed',
              images: [...detailImageUrls]
            };

            // 옵션의 coupangDoc 업데이트
            if (option.coupangDoc && option.coupangDoc.jsonDocument &&
                option.coupangDoc.jsonDocument.length > 0) {
              const optionDocObj = option.coupangDoc.jsonDocument[0];
              if (optionDocObj.imagePage && optionDocObj.imagePage.details) {
                if (combinedImageUrl) {
                  // 통합된 이미지 URL 설정
                  optionDocObj.imagePage.details.detailedImage = combinedImageUrl;
                  optionDocObj.imagePage.details.htmlProductDetailContent = "";
                  console.log(`옵션 coupangDoc.detailedImage 설정: ${combinedImageUrl.substring(0, 50)}...`);
                } else if (detailImageUrls && detailImageUrls.length > 0) {
                  // 통합 이미지 생성 실패 시 콤마로 구분된 URL로 폴백
                  const fallbackUrl = detailImageUrls.join(',');
                  optionDocObj.imagePage.details.detailedImage = fallbackUrl;
                  optionDocObj.imagePage.details.htmlProductDetailContent = "";
                  console.log(`통합 이미지 생성 실패, 콤마로 구분된 URL 사용: ${fallbackUrl.substring(0, 50)}...`);
                }

                // altText 설정 — 원제목(1688 중국어)은 안 씀 · 옵션 이름 붙임(detailAltText)
                optionDocObj.imagePage.details.altText = detailAltText(updatedProduct, option.korName);
              }
            }
          });
        }

        // Vuex 액션으로 상품 업데이트
        await this.updateRocketProduct(updatedProduct);

        if (combinedFailReason) {
          this.$bvToast.toast(`통합 이미지를 못 만들어 개별 이미지로 저장했어요: ${combinedFailReason}`, {
            title: '저장(경고)',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 6000
          });
        } else {
          // 성공 메시지
          this.$bvToast.toast('상세 페이지가 저장되었습니다', {
            title: '저장 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
        }
      } catch (error) {
        console.error('상세 페이지 저장 오류:', error);
        this.$bvToast.toast(`상세 페이지 저장 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 로딩 상태 해제
        this.setProcessing({
          isProcessing: false,
          message: ''
        });
        this.isProcessing = false;
      }
    },

    // 서버가 준 까닭(응답 본문 message) — 없으면 오류 글 그대로
    serverReason(error) {
      const data = error && error.response && error.response.data;
      return (data && (data.message || data.alert || data.error)) || (error && error.message) || '알 수 없는 오류';
    },

    // 다음 단계로 이동
    goToNextStep() {
      this.$emit('next');
    },

    // 상세 이미지 다운로드
    async downloadDetailImage() {
      try {
        // 로딩 상태 설정
        this.isProcessing = true;
        this.processingMessage = '이미지 다운로드 중...';

        // coupangDoc에서 detailedImage URL 가져오기
        let imageUrl = '';

        // 1. 일반 상품의 coupangDoc 확인
        if (this.product?.coupangDoc?.jsonDocument?.length > 0) {
          const docObj = this.product.coupangDoc.jsonDocument[0];
          if (docObj?.imagePage?.details?.detailedImage) {
            imageUrl = docObj.imagePage.details.detailedImage;
            console.log('쿠팡 상세 이미지 URL 발견:', imageUrl.substring(0, 50) + '...');
          }
        }

        // 2. 옵션이 있는 상품의 경우 options 안의 coupangDoc 확인
        if (!imageUrl && this.product?.options?.length > 0) {
          // 첫 번째 옵션의 coupangDoc 확인
          const option = this.product.options[0];
          if (option?.coupangDoc?.jsonDocument?.length > 0) {
            const optionDocObj = option.coupangDoc.jsonDocument[0];
            if (optionDocObj?.imagePage?.details?.detailedImage) {
              imageUrl = optionDocObj.imagePage.details.detailedImage;
              console.log('옵션의 쿠팡 상세 이미지 URL 발견:', imageUrl.substring(0, 50) + '...');
            }
          }
        }

        // URL이 없으면 오류 발생
        if (!imageUrl) {
          // 저장된 이미지가 없는 경우, 현재 화면에 표시된 이미지들을 기반으로 새로 생성
          console.log('저장된 이미지 URL을 찾을 수 없어 현재 이미지로 새로 생성합니다.');

          // 설정에서 상단/하단 이미지 URL 가져오기
          const settings = this.defaultSettings.imageSettings || {};
          const topImageUrl = settings.useTopImage && settings.topImageUrl ? settings.topImageUrl : null;
          const bottomImageUrl = settings.useBottomImage && settings.bottomImageUrl ? settings.bottomImageUrl : null;

          // 처리된 이미지 목록 가져오기
          const processedImages = this.processedGroups.filter(group => !group.isSettingImage);

          if (processedImages.length === 0) {
            throw new Error('다운로드할 이미지가 없습니다. 먼저 이미지를 업로드하고 저장해주세요.');
          }

          // 병합용 HTML 생성 - 간격 0px로 설정
          let combinedHtml = `
            <div style="width:800px; margin:0; padding:0; background-color:#ffffff; box-sizing:border-box;">
          `;

          // 상단 이미지 추가 (있으면)
          if (topImageUrl) {
            combinedHtml += `
            <div style="margin:0; padding:0; text-align:center; width:800px;">
              <img src="${topImageUrl}" alt="상단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
            </div>
            `;
          }

          // 처리된 이미지 추가
          processedImages.forEach((image, index) => {
            combinedHtml += `
            <div style="margin:0; padding:0; text-align:center; width:800px;">
              <img src="${image.url}" alt="상세 이미지 ${index+1}" style="width:800px; display:block; margin:0 auto; padding:0;">
            </div>
            `;
          });

          // 하단 이미지 추가 (있으면)
          if (bottomImageUrl) {
            combinedHtml += `
            <div style="margin:0; padding:0; text-align:center; width:800px;">
              <img src="${bottomImageUrl}" alt="하단 이미지" style="width:800px; display:block; margin:0 auto; padding:0;">
            </div>
            `;
          }

          combinedHtml += '</div>';

          // HTML을 이미지로 변환
          imageUrl = await detailImageService.convertHtmlToImage(combinedHtml, this.product?.registrationId || 'detail');

          if (!imageUrl) {
            throw new Error('이미지 생성에 실패했습니다.');
          }

          console.log('새로 생성된 이미지 URL:', imageUrl.substring(0, 50) + '...');
        } else {
          // 콤마로 구분된 URL이면 첫 번째 URL만 사용
          if (imageUrl.includes(',')) {
            imageUrl = imageUrl.split(',')[0];
            console.log('콤마로 구분된 URL에서 첫 번째 URL만 사용:', imageUrl);
          }
        }

        // 이미지 다운로드
        const imageBlob = await imageService.downloadImage(imageUrl);

        // 파일명 생성
        const productId = this.product?.registrationId || 'detail';
        const fileName = `${productId}_detail_full_${Date.now()}.jpg`;

        // Blob URL 생성 및 다운로드
        const blobUrl = URL.createObjectURL(imageBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // 정리
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        // 성공 메시지
        this.$bvToast.toast('상세 이미지가 다운로드되었습니다', {
          title: '다운로드 완료',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 2000
        });
      } catch (error) {
        console.error('이미지 다운로드 오류:', error);
        this.$bvToast.toast(`이미지 다운로드 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        });
      } finally {
        // 로딩 상태 해제
        this.isProcessing = false;
        this.processingMessage = '';
      }
    }
  }
};
</script>

<style scoped>
.claim-warning__body {
  display: block;
  width: 100%;
}
.claim-warning__title {
  font-weight: 600;
}
.claim-warning__list {
  padding-left: 1.2rem;
  font-size: 0.85rem;
}
.claim-warning__reason {
  color: #6c757d;
}
.detail-image-edit {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 로딩 컨테이너 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: #f7f7f7;
}

/* 상단 헤더 영역 */
.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  z-index: 10;
  flex-shrink: 0;
}

.editor-title {
  flex: 0 0 200px;
}

.editor-title h5 {
  margin: 0;
  font-weight: 600;
}

.editor-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
}

/* 미리보기 모드 */
.preview-mode {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.preview-container {
  flex: 1;
  overflow-y: auto;
  background-color: #f5f5f5;
}

.preview-scroll-container {
  display: flex;
  justify-content: center;
}

.detail-content-preview {
  width: 100%;
  max-width: 800px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 편집 모드 */
.edit-mode {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* 상단 헤더 */
.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  z-index: 10;
}

.editor-title {
  flex: 0 0 auto;
}

.editor-title h5 {
  margin: 0;
  font-weight: 600;
}

.editor-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 메인 컨텐츠 영역 */
.editor-content {
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
}

/* 이미지 뷰어 컨테이너 */
.image-viewer-container {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 처리된 이미지 섹션 */
.processed-images-section {
  height: 100%;
  overflow: hidden;
}

.processed-images-scroller {
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 0;
  background-color: #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.processed-images-container {
  width: 800px;
  margin: 0 auto;
  background-color: white;
}

.processed-image-item {
  position: relative;
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
}

.processed-image {
  width: 100%;
  height: auto;
  display: block;
}

/* 마우스 호버 시 표시되는 오버레이 */
.hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.processed-image-item:hover .hover-overlay,
.original-image-item:hover .hover-overlay {
  opacity: 1;
}

/* 오버레이 내 액션 버튼 레이아웃 */
.overlay-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 300px;
  padding: 15px;
}

.action-button {
  width: 100%;
}

/* 관리 버튼 그룹 */
.manage-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  width: 100%;
  margin-top: 12px;
}

.manage-buttons .btn {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 4px;
}

/* 아이콘 버튼 공통 스타일 */
.action-icon-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  transition: all 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
}

.action-icon-button i {
  font-size: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

/* 위로 이동 버튼 */
.action-icon-up {
  color: #007bff;
}

.action-icon-up:hover:not(:disabled) {
  background-color: #007bff;
  color: white;
  transform: translateY(-2px);
}

.action-icon-up:disabled {
  opacity: 0.5;
  color: #aaa;
}

/* 아래로 이동 버튼 */
.action-icon-down {
  color: #007bff;
}

.action-icon-down:hover:not(:disabled) {
  background-color: #007bff;
  color: white;
  transform: translateY(2px);
}

.action-icon-down:disabled {
  opacity: 0.5;
  color: #aaa;
}

/* 삭제 버튼 */
.action-icon-delete {
  color: #dc3545;
}

.action-icon-delete:hover {
  background-color: #dc3545;
  color: white;
  transform: scale(1.1);
}

/* Button icon styles moved to .action-icon-button */

/* 모바일 환경 최적화 */
@media (max-width: 576px) {
  .overlay-actions {
    padding: 10px;
    max-width: 250px;
  }

  .action-button {
    font-size: 14px;
    padding: 6px 10px;
  }

  .manage-buttons {
    flex-wrap: wrap;
    justify-content: center;
  }

  .action-icon-button {
    width: 32px;
    height: 32px;
  }

  .action-icon-button i {
    font-size: 16px;
  }
}

/* 액션 버튼 공통 스타일 - 아이콘 버튼 전용 스타일로 대체 */

/* 원본 이미지 섹션 */
.original-images-section {
  height: 100%;
  overflow: hidden;
}

.original-images-scroller {
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 0;
  background-color: #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.original-images-container {
  width: 800px;
  margin: 0 auto;
  background-color: white;
}

/* 불필요한 스타일 제거 */

.original-image-item {
  margin: 0;
  padding: 0;
  position: relative;
  cursor: pointer;
}

.original-image {
  width: 100%;
  height: auto;
  display: block;
}

/* 원본 이미지 오버레이 스타일 */
.original-image-item .hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* 이미지가 없을 때 */
.no-images-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #999;
  text-align: center;
  padding: 50px 20px;
}

.no-images-placeholder i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #ccc;
}

/* 이미지 편집기 전체 화면 */
.image-container-full {
  height: 100%;
  width: 100%;
}

/* 로딩 오버레이 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
  padding: 25px 30px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.loading-content p {
  margin-top: 15px;
  color: #333;
  font-weight: 500;
}

/* 이미지 번호 배지 */
/* 이미지 번호 배지 스타일 제거 */

/* 로딩 오버레이 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.loading-content {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.loading-content p {
  margin-top: 15px;
  color: #333;
}

/* 이미지 타입 배지 스타일 */
.image-type-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
  z-index: 5;
}

.top-badge {
  background-color: #007bff;
}

.bottom-badge {
  background-color: #6610f2;
}

.original-badge {
  background-color: #6c757d;
}

.translated-badge {
  background-color: #28a745;
}

/* 결과 이미지 배지 제거 */

/* 설정 이미지 스타일 */
.setting-image-item {
  position: relative;
  border: 2px solid #007bff;
}

.top-image-item {
  border-color: #007bff;
}

.bottom-image-item {
  border-color: #6610f2;
}

/* 결과 이미지 스타일 제거 */

/* 반응형 레이아웃 */
@media (max-width: 992px) {
  .editor-layout {
    flex-direction: column;
  }

  .images-panel {
    width: 100%;
    height: 300px;
    border-right: none;
    border-bottom: 1px solid #eee;
  }

  .editor-action-buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
    border-left: none;
    border-top: 1px solid #eee;
    padding: 10px;
  }

  .editor-action-buttons .btn {
    margin-top: 0 !important;
    flex: 1;
    margin-right: 10px;
  }

  .editor-action-buttons .btn:last-child {
    margin-right: 0;
  }
}

@media (max-width: 768px) {
  .editor-header {
    flex-wrap: wrap;
  }

  .editor-title {
    flex: 1 0 100%;
    margin-bottom: 10px;
  }

  .editor-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-top: 10px;
  }
}

/* 개별 이미지 로딩 오버레이 스타일 */
.processed-image-item {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.loading-spinner {
  text-align: center;
}

.loading-text {
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}

.processed-image.loading {
  opacity: 0.5;
  filter: blur(1px);
}

/* 로딩 중일 때 호버 효과 비활성화 */
.processed-image-item:has(.loading-overlay) .hover-overlay {
  display: none !important;
}

/* 이미지 편집기 모달 스타일 */
::v-deep .image-editor-modal .modal-dialog {
  max-width: 95vw !important;
  width: 95vw !important;
  height: 95vh !important;
  margin: 2.5vh auto !important;
}

::v-deep .image-editor-modal .modal-content {
  height: 100% !important;
  border-radius: 8px !important;
}

::v-deep .image-editor-modal .modal-header {
  padding: 1rem 1.5rem !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep .image-editor-modal-body {
  padding: 0 !important;
  height: calc(100% - 60px) !important;
  overflow: hidden !important;
}

::v-deep .image-editor-modal .image-editor-container {
  width: 100% !important;
  height: 100% !important;
  min-height: unset !important;
}

::v-deep .image-editor-modal #xiangji-image-editor {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
}

.sibling-list {
  max-height: 320px;
  overflow-y: auto;
}

.sibling-item {
  padding: 6px 4px;
  border-bottom: 1px solid #f0f0f0;
}

.sibling-item:last-child {
  border-bottom: none;
}

.sibling-name {
  font-size: 13px;
}
</style>